import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useNavigate, useParams, useSearchParams, useLocation } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import { ArrowLeft, Upload, Image, X, Check, CheckCircle, FileText, Calendar, Clock, Palette, Send, MapPin, Tag, MessageSquare, Play, Video, ChevronDown, ChevronUp, ChevronLeft, ChevronRight, User, ShieldCheck, AlertCircle, History, Search, Globe, Plus, Edit3, Trash2, CheckCheck } from 'lucide-react';
import { Facebook, Instagram, Linkedin, Youtube, Twitter } from 'lucide-react';
import ContentCreatorLayout from './Layout';
import { validateMediaForPlatforms, validateThumbnail, hasMixedContent, hasMultipleVideos, validateMediaSelectionForPostType } from './mediaValidation';

// Helper to get creator email from localStorage
function getCreatorEmail() {
  let email = '';
  try {
    email = (localStorage.getItem('userEmail') || '').toLowerCase();
    if (!email) {
      const userStr = localStorage.getItem('user');
      if (userStr) {
        const userObj = JSON.parse(userStr);
        if (userObj.email) {
          email = userObj.email.toLowerCase();
        }
      }
    }
  } catch (e) {
    email = '';
  }
  return email;
}

// Helper to get current user role from localStorage
function getUserRole() {
  try {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      const userObj = JSON.parse(userStr);
      return (userObj.role || '').toLowerCase();
    }
  } catch (e) { }
  return '';
}

function normalizeReviewTabMode(value) {
  if (value === 'admin' || value === 'customer' || value === 'both') return value;
  return 'both';
}

function ContentUpload() {
  const navigate = useNavigate();
  const location = useLocation();
  const { calendarId, itemIndex } = useParams();
  const [searchParams] = useSearchParams();
  const fileInputRef = useRef(null);
  const thumbnailInputRef = useRef(null);
  const isAdminPortal = useMemo(() => location.pathname.startsWith('/admin'), [location.pathname]);
  const isAdmin = useMemo(() => getUserRole() === 'admin' || window.location.hash.includes('admin') || isAdminPortal, [isAdminPortal]);
  const [activeTab, setActiveTab] = useState(() => (getUserRole() === 'admin' || window.location.hash.includes('admin') || window.location.pathname.startsWith('/admin')) ? 'customer' : 'admin');
  const [reviewTabMode, setReviewTabMode] = useState('both');

  // State for assignment details
  const [assignment, setAssignment] = useState(null);
  const [loading, setLoading] = useState(true);

  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [thumbnailFileObj, setThumbnailFileObj] = useState(null);
  const [thumbnailFileRemoved, setThumbnailFileRemoved] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const replaceFileInputRef = useRef(null);
  const [replacingFileId, setReplacingFileId] = useState(null);
  const [caption, setCaption] = useState('');
  const [hashtags, setHashtags] = useState('');
  const [notes, setNotes] = useState('');
  const [geoLocation, setGeoLocation] = useState({ latitude: '', longitude: '' });
  const [address, setAddress] = useState('');
  const [contactInfo, setContactInfo] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [previousSubmissionLoaded, setPreviousSubmissionLoaded] = useState(false);
  const [selectedMedia, setSelectedMedia] = useState(null);
  const [allPreviousVersions, setAllPreviousVersions] = useState([]);
  // Compute admin-stage versions
  const adminVersions = useMemo(() => {
    return allPreviousVersions
      .filter(v => v.submissionStage !== 'customer' || v.sentToCustomerAt)
      .map((v, idx) => ({
        ...v,
        flowVersionNumber: idx + 1,
        versionNumber: idx + 1
      }));
  }, [allPreviousVersions]);

  // Compute customer-stage versions
  const customerVersions = useMemo(() => {
    return allPreviousVersions
      .filter(v => v.submissionStage === 'customer' || v.submissionStage === '')
      .map((v, idx) => ({
        ...v,
        flowVersionNumber: idx + 1,
        versionNumber: idx + 1
      }));
  }, [allPreviousVersions]);

  // Derive previousVersions from activeTab for easy drop-in compatibility
  const previousVersions = useMemo(() => {
    return activeTab === 'admin' ? adminVersions : customerVersions;
  }, [activeTab, adminVersions, customerVersions]);

  const isRevisionRequested = useMemo(() => {
    if (allPreviousVersions.length === 0) return false;
    const latestVersion = allPreviousVersions[allPreviousVersions.length - 1];
    const revisionStatuses = ['revision_requested', 'changes_requested', 'changes_requested_admin', 'changes_requested_customer_approved_admin'];
    return revisionStatuses.includes(latestVersion?.status);
  }, [allPreviousVersions]);

  const hasModifiedMedia = useMemo(() => {
    return uploadedFiles.some(f => !f.isExisting) || thumbnailFileObj !== null || thumbnailFileRemoved;
  }, [uploadedFiles, thumbnailFileObj, thumbnailFileRemoved]);

  const [versionsAccordionOpen, setVersionsAccordionOpen] = useState(true);
  const [selectedVersionIndex, setSelectedVersionIndex] = useState(0);
  const selectedVersionId = previousVersions[selectedVersionIndex]?.id || null;
  const [selectedMediaIndex, setSelectedMediaIndex] = useState(0);
  const [validationNotice, setValidationNotice] = useState('');
  const [commentsForVersion, setCommentsForVersion] = useState([]);
  const [commentsForCurrentMedia, setCommentsForCurrentMedia] = useState([]);

  const otherSlidesWithComments = useMemo(() => {
    const unresolvedComments = commentsForVersion.filter(c => {
      return !c.done && c.status !== 'completed' && !c.discarded && !c.finalized;
    });
    const otherSlides = unresolvedComments
      .filter(c => {
        const idx = Number(c.mediaIndex !== undefined ? c.mediaIndex : 0);
        return idx !== selectedMediaIndex;
      })
      .map(c => Number(c.mediaIndex !== undefined ? c.mediaIndex : 0) + 1);
    return [...new Set(otherSlides)].sort((a, b) => a - b);
  }, [commentsForVersion, selectedMediaIndex]);
  const [activeVersionComment, setActiveVersionComment] = useState(null);
  const [hoveredVersionComment, setHoveredVersionComment] = useState(null);
  const [versionImgDimensions, setVersionImgDimensions] = useState(null);
  const [replyingToComment, setReplyingToComment] = useState(null);
  const [editingReplyId, setEditingReplyId] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [replySubmitting, setReplySubmitting] = useState(false);
  const versionImgRef = useRef(null);
  const videoRef = useRef(null);

  // Send-to-customer state
  const [sendingToCustomer, setSendingToCustomer] = useState(false);
  const [sentToCustomer, setSentToCustomer] = useState(false);
  const [adminUploadedForThis, setAdminUploadedForThis] = useState(false);
  const [submitSuccessModal, setSubmitSuccessModal] = useState({
    open: false,
    title: '',
    message: ''
  });

  // Admin notification state
  const [adminsList, setAdminsList] = useState([]);
  const [, setAdminsLoading] = useState(false);

  const displayAdminsList = useMemo(() => {
    if (!assignment || !assignment.customerId) return [];
    return adminsList.filter(admin =>
      admin.assignedCustomers && admin.assignedCustomers.includes(assignment.customerId)
    );
  }, [adminsList, assignment]);

  const creatorEmail = getCreatorEmail();
  const currentUser = useMemo(() => {
    try {
      const raw = localStorage.getItem('user');
      const parsed = raw ? JSON.parse(raw) : null;
      return {
        email: String(parsed?.email || '').toLowerCase(),
        name: String(parsed?.name || '').toLowerCase(),
      };
    } catch {
      return { email: '', name: '' };
    }
  }, []);
  const currentUserEmail = currentUser.email || creatorEmail;
  const normalizedPostType = useMemo(() => (assignment?.postType || '').toLowerCase(), [assignment?.postType]);
  const isSingleMediaPostType = useMemo(() => ['post', 'story', 'reel', 'video'].includes(normalizedPostType), [normalizedPostType]);
  const canAddMoreMedia = useMemo(
    () => !isSingleMediaPostType || uploadedFiles.length < 1,
    [isSingleMediaPostType, uploadedFiles.length]
  );

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!creatorEmail) {
      navigate('/content-creator/login');
    }
  }, [creatorEmail, navigate]);

  // Fetch list of admins for notification selector
  useEffect(() => {
    const fetchAdmins = async () => {
      setAdminsLoading(true);
      try {
        const res = await fetch(`${process.env.REACT_APP_API_URL}/users?role=admin`);
        if (res.ok) {
          const data = await res.json();
          const admins = Array.isArray(data) ? data : [];
          setAdminsList(admins.map(a => ({
            id: a._id || a.id,
            name: a.name || a.email || 'Admin',
            email: a.email || '',
            assignedCustomers: a.assignedCustomers || []
          })).filter(a => a.email));
        }
      } catch (err) {
        console.error('Ã¢ÂÅ’ Failed to fetch admins:', err);
      } finally {
        setAdminsLoading(false);
      }
    };
    fetchAdmins();
  }, []);



  // Assignment picker state (used when no params provided)
  const [pickerAssignments, setPickerAssignments] = useState([]);
  const [pickerCustomers, setPickerCustomers] = useState([]);
  const [pickerLoading, setPickerLoading] = useState(false);
  const [pickerSearch, setPickerSearch] = useState('');
  const [pickerPlatform, setPickerPlatform] = useState('all');
  const [pickerStatus, setPickerStatus] = useState('all');
  const [pickerCustomer, setPickerCustomer] = useState(() => searchParams.get('customer') || 'all');
  const [pickerSort, setPickerSort] = useState('due');
  const [pickerSubmissions, setPickerSubmissions] = useState([]);
  const [pickerScheduledPosts, setPickerScheduledPosts] = useState([]);

  // Helper: flatten platform (string or array) into a lowercase string array
  const flatPlatforms = (val) => {
    if (!val) return [];
    if (Array.isArray(val)) return val.map(v => String(v).trim().toLowerCase()).filter(Boolean);
    return [String(val).trim().toLowerCase()];
  };

  const getValidationPlatforms = (val) => {
    if (!val) return [];
    const asString = Array.isArray(val) ? val.join(',') : String(val);
    const matches = asString.match(/facebook|instagram|youtube|linkedin|twitter|tiktok|pinterest/ig);
    if (matches) {
      return [...new Set(matches.map(match => match.toLowerCase()))];
    }
    return flatPlatforms(val);
  };

  // Helper: get normalized display status (mirrors Assignments.jsx getFilterStatus)
  const getPickerFilterStatus = (a) => {
    // Check published flag or scheduled post
    if (a.published === true) return 'published';
    const aid = a.id || a._id || '';
    if (aid && pickerScheduledPosts.some(post => post.contentId === aid && post.status === 'published')) return 'published';
    // Check customer-approval via submissions
    const subs = pickerSubmissions.filter(sub => {
      const subCal = sub.calendar_id || sub.calendarId;
      const subIdx = sub.item_index !== undefined ? String(sub.item_index) : undefined;
      if (subCal && subIdx !== undefined) {
        return subCal === a.calendarId && subIdx === String(a.itemIndex);
      }
      const subId = sub.assignment_id || sub.assignmentId;
      return subId && String(subId) === String(a.id);
    });
    const isCustomerApproved = subs.some(s => (s.approved_by_customer === true || s.status === 'approved_customer' || s.status === 'approved_both') && !['under_review', 'sent_to_creator', 'revision_requested', 'rejected', 'customer_feedback_pending_admin', 'pending_customer_review', 'changes_requested_admin', 'changes_requested_customer_approved_admin'].includes(s.status));
    if (isCustomerApproved) return 'approved';
    return 'pending';
  };

  const getPickerItemApproval = (a) => {
    const filterStatus = getPickerFilterStatus(a);
    if (filterStatus === 'published') {
      return {
        label: 'Published',
        color: 'bg-purple-100 text-purple-800'
      };
    }
    if (filterStatus === 'pending') {
      return {
        label: 'Pending',
        color: 'bg-orange-100 text-orange-800'
      };
    }

    const subs = pickerSubmissions.filter(sub => {
      const subCal = sub.calendar_id || sub.calendarId;
      const subIdx = sub.item_index !== undefined ? String(sub.item_index) : undefined;
      if (subCal && subIdx !== undefined) {
        return subCal === a.calendarId && subIdx === String(a.itemIndex);
      }
      const subId = sub.assignment_id || sub.assignmentId;
      return subId && String(subId) === String(a.id);
    });

    const isCustomerApproved = subs.some(s => (s.approved_by_customer === true || s.status === 'approved_customer' || s.status === 'approved_both') && !['under_review', 'sent_to_creator', 'revision_requested', 'rejected', 'customer_feedback_pending_admin', 'pending_customer_review', 'changes_requested_admin', 'changes_requested_customer_approved_admin'].includes(s.status)) || a.status === 'approved';
    const isAdminApproved = subs.some(s => (s.approved_by_admin === true || s.status === 'approved_admin' || s.status === 'approved_both' || (s.status === 'approved' && !s.approved_by_customer) || (s.submission_stage || s.submissionStage || '') === 'customer') && !['revision_requested', 'rejected', 'customer_feedback_pending_admin', 'changes_requested_admin', 'changes_requested_customer_approved_admin'].includes(s.status));

    if (isCustomerApproved && isAdminApproved) {
      return {
        label: 'Both Approved',
        color: 'bg-green-100 text-green-800'
      };
    }
    if (isCustomerApproved) {
      return {
        label: 'Customer Approved',
        color: 'bg-green-100 text-green-800'
      };
    }
    if (isAdminApproved) {
      return {
        label: 'Admin Approved',
        color: 'bg-indigo-100 text-indigo-800'
      };
    }

    return {
      label: 'Approved',
      color: 'bg-green-100 text-green-800'
    };
  };

  // Helper: get latest internal-stage submission status for a picker assignment
  const getPickerReviewStatus = (a) => {
    const subs = pickerSubmissions.filter(sub => {
      const subCal = sub.calendar_id || sub.calendarId;
      const subIdx = sub.item_index !== undefined ? String(sub.item_index) : undefined;
      if (subCal && subIdx !== undefined) {
        return subCal === a.calendarId && subIdx === String(a.itemIndex);
      }
      const subId = sub.assignment_id || sub.assignmentId;
      return subId && String(subId) === String(a.id);
    });
    if (subs.length === 0) return null;
    const latest = [...subs].sort((x, y) => new Date(y.created_at || 0) - new Date(x.created_at || 0))[0];
    const isCustApproved = latest.approved_by_customer === true || latest.status === 'approved_customer' || latest.status === 'approved_both';
    if (isCustApproved) {
      return { label: 'Customer Approved', color: 'bg-green-100 text-green-700' };
    }
    if (latest.submission_stage === 'customer' || latest.submissionStage === 'customer') {
      return { label: 'Admin Directly Sent to Customer', color: 'bg-blue-100 text-blue-700' };
    }
    if (latest.status === 'approved') {
      return { label: 'Approved by Admin', color: 'bg-orange-100 text-orange-700' };
    }
    if (latest.status === 'revision_requested') {
      return { label: 'Revision Requested', color: 'bg-red-100 text-red-700' };
    }
    return { label: 'Under Admin Review', color: 'bg-amber-100 text-amber-700' };
  };

  const selectedCustomer = useMemo(() => {
    if (!pickerCustomer || pickerCustomer === 'all') return null;
    return pickerCustomers.find(c => (c._id || c.id) === pickerCustomer);
  }, [pickerCustomers, pickerCustomer]);

  const pickerStats = useMemo(() => {
    const customerAssignments = pickerAssignments.filter(a => pickerCustomer === 'all' || a.customerId === pickerCustomer);

    const adminApprovedCount = customerAssignments.filter(a => {
      const subs = pickerSubmissions.filter(sub => {
        const subCal = sub.calendar_id || sub.calendarId;
        const subIdx = sub.item_index !== undefined ? String(sub.item_index) : undefined;
        if (subCal && subIdx !== undefined) {
          return subCal === a.calendarId && subIdx === String(a.itemIndex);
        }
        const subId = sub.assignment_id || sub.assignmentId;
        return subId && String(subId) === String(a.id);
      });
      return subs.some(s => s.status === 'approved' && (s.submission_stage || s.submissionStage || 'internal') !== 'customer');
    }).length;

    return {
      total: customerAssignments.length,
      pending: customerAssignments.filter(a => getPickerFilterStatus(a) === 'pending').length,
      approved: customerAssignments.filter(a => getPickerFilterStatus(a) === 'approved').length,
      published: customerAssignments.filter(a => getPickerFilterStatus(a) === 'published').length,
      adminApproved: adminApprovedCount
    };
  }, [pickerAssignments, pickerSubmissions, pickerScheduledPosts, pickerCustomer]);

  // When no params, fetch assignments for the picker
  useEffect(() => {
    if (calendarId && itemIndex !== undefined) return;
    const fetchPicker = async () => {
      setPickerLoading(true);
      try {
        const [custRes, calRes, subRes, schedRes] = await Promise.all([
          fetch(`${process.env.REACT_APP_API_URL}/api/customers`),
          fetch(`${process.env.REACT_APP_API_URL}/calendars`),
          fetch(`${process.env.REACT_APP_API_URL}/api/content-submissions`),
          fetch(`${process.env.REACT_APP_API_URL}/api/scheduled-posts`),
        ]);
        const custData = await custRes.json();
        const calendars = await calRes.json();
        const subData = await subRes.json();
        const schedData = schedRes.ok ? await schedRes.json() : [];
        setPickerCustomers(custData.customers || []);
        const customerMap = {};
        if (Array.isArray(custData.customers)) {
          custData.customers.forEach(c => {
            customerMap[c._id || c.id] = c.name || c.customerName || c.email || '';
          });
        }
        setPickerSubmissions(Array.isArray(subData) ? subData : (subData.submissions || []));
        setPickerScheduledPosts(Array.isArray(schedData) ? schedData : []);
        const all = [];
        calendars.forEach(calendar => {
          if (Array.isArray(calendar.contentItems)) {
            calendar.contentItems.forEach((item, index) => {
              if ((item.assignedTo || '').toLowerCase() === creatorEmail) {
                const custId = calendar.customerId || calendar.customer_id || calendar.customer?._id || '';
                all.push({
                  ...item,
                  calendarId: calendar._id || calendar.id,
                  calendarName: calendar.name || calendar.customerName || calendar.customer || '',
                  customerName: customerMap[custId] || calendar.customerName || calendar.name || '',
                  customerId: custId,
                  itemIndex: index,
                  id: item.id || item._id || `${calendar._id || calendar.id}::${index}`,
                  stableItemId: item.id || item._id || `${calendar._id || calendar.id}::${index}`,
                  platform: item.platform || item.type || 'Instagram',
                  dueDate: item.dueDate || item.due_date || item.date,
                });
              }
            });
          }
        });
        setPickerAssignments(all);
      } catch (err) {
        setPickerAssignments([]);
      } finally {
        setPickerLoading(false);
      }
    };
    if (creatorEmail) fetchPicker();
  }, [calendarId, itemIndex, creatorEmail]);

  // Reset version state whenever we switch to a different assignment
  useEffect(() => {
    setPreviousSubmissionLoaded(false);
    setAllPreviousVersions([]);
    setSelectedVersionIndex(0);
    setSelectedMediaIndex(0);
    setCommentsForVersion([]);
    setCommentsForCurrentMedia([]);
    setSentToCustomer(false);
    setAdminUploadedForThis(false);
    setReviewTabMode('both');
    setThumbnailFileObj(null);
    setThumbnailFileRemoved(false);
  }, [calendarId, itemIndex]);

  // Fetch real assignment data based on calendarId and itemIndex
  useEffect(() => {
    const fetchAssignment = async () => {
      setLoading(true);
      try {
        // First fetch customers to build a map
        const customersRes = await fetch(`${process.env.REACT_APP_API_URL}/api/customers`);
        const customersData = await customersRes.json();

        const customerMap = {};
        if (Array.isArray(customersData.customers)) {
          customersData.customers.forEach(c => {
            customerMap[c._id || c.id] = {
              name: c.name || c.customerName || c.email || '',
              email: c.email || '',
              id: c._id || c.id,
              reviewTabMode: normalizeReviewTabMode(c.reviewTabMode)
            };
          });
        }

        // Then fetch the specific calendar
        const res = await fetch(`${process.env.REACT_APP_API_URL}/calendars`);
        const calendars = await res.json();

        let found = null;
        calendars.forEach((calendar) => {
          const customerId = calendar.customerId || calendar.customer_id || calendar.customer?._id || '';
          const customerInfo = customerMap[customerId] || {};

          if (Array.isArray(calendar.contentItems)) {
            // Use itemIndex to get the correct item
            const idx = parseInt(itemIndex, 10);
            if (!isNaN(idx) && calendar._id === calendarId && calendar.contentItems[idx]) {
              const item = calendar.contentItems[idx];
              const stableItemId = item.id || item._id || `${calendar._id}::${idx}`;
              found = {
                ...item,
                calendarName: calendar.name || calendar.customerName || calendar.customer || '',
                calendarId: calendar._id,
                customerId: customerId,
                customerName: customerInfo.name || calendar.customerName || calendar.name || '',
                customerEmail: customerInfo.email || '',
                id: stableItemId,
                stableItemId,
                itemIndex: idx,
                dueDate: item.dueDate || item.due_date || item.date,
                platform: item.platform || item.type || 'Instagram',
                requirements: item.requirements || [],
                reviewTabMode: normalizeReviewTabMode(customerInfo.reviewTabMode),
                createdAt: item.createdAt || item.created_at || null,
              };

              // Only log if customer data is missing
              if (!found.customerId) {
                console.error('Ã¢ÂÅ’ Customer ID is missing for calendarId:', calendarId, 'itemIndex:', itemIndex);
              }
              if (!found.customerName) {
                console.error('Ã¢ÂÅ’ Customer name is missing for calendarId:', calendarId, 'itemIndex:', itemIndex);
              }
            }
          }
        });

        if (!found) {
          console.error('Ã¢ÂÅ’ Assignment not found for calendarId:', calendarId, 'itemIndex:', itemIndex);
        }

        setAssignment(found);
        const initialMode = normalizeReviewTabMode(found?.reviewTabMode) || 'admin';
        setActiveTab(initialMode === 'both' ? 'admin' : initialMode);
        setReviewTabMode(initialMode);
      } catch (err) {
        console.error('Ã¢ÂÅ’ Error fetching assignment:', err);
        setAssignment(null);
      } finally {
        setLoading(false);
      }
    };
    if (calendarId && itemIndex !== undefined) {
      fetchAssignment();
    }
  }, [calendarId, itemIndex]);

  // Fetch previous submission to pre-fill caption and hashtags for revisions
  useEffect(() => {
    const fetchPreviousSubmission = async () => {
      if (!assignment || !assignment.id || previousSubmissionLoaded) {
        console.log('Ã¢ÂÂ­Ã¯Â¸Â Skipping fetch - assignment:', !!assignment, 'id:', assignment?.id, 'loaded:', previousSubmissionLoaded);
        return;
      }

      try {
        const normalizeUrlForCompare = (value) => {
          if (!value || typeof value !== 'string') return '';
          return value.split('?')[0].split('#')[0].trim().toLowerCase();
        };

        const normMedia = (media, thumbnailUrl = '') => {
          if (!media || !Array.isArray(media)) return [];
          const isReelOrVideoPost = ['reel', 'video'].includes((assignment?.postType || '').toLowerCase());
          const getType = (url) => {
            if (!url || typeof url !== 'string') return 'image';
            const ext = url.toLowerCase().split('.').pop();
            return ['mp4', 'webm', 'ogg', 'mov', 'avi'].includes(ext) ? 'video' : 'image';
          };

          const normalizedThumbUrl = normalizeUrlForCompare(thumbnailUrl);

          const cleaned = media.map(item => {
            if (typeof item === 'string') {
              return { url: item, type: getType(item) };
            }
            if (item?.url && typeof item.url === 'string') {
              return {
                url: item.url,
                type: item.type || getType(item.url),
                isThumbnail: item.isThumbnail === true || item.kind === 'thumbnail' || item.role === 'thumbnail'
              };
            }
            return null;
          }).filter(item => {
            if (!item) return false;
            if (item.isThumbnail) return false;
            if (normalizedThumbUrl && normalizeUrlForCompare(item.url) === normalizedThumbUrl) return false;
            return true;
          });

          // Reel/Video submissions should only show actual video media in the main media carousel.
          if (isReelOrVideoPost) {
            const videosOnly = cleaned.filter(item => {
              if (item.type === 'video') return true;
              return /\.(mp4|webm|ogg|mov|avi|mkv)(\?|$)/i.test(item.url || '');
            });
            return videosOnly.length > 0 ? videosOnly : cleaned;
          }

          return cleaned;
        };

        console.log('Ã°Å¸â€Â Fetching previous submissions for assignment:', assignment.id, 'by creator:', creatorEmail);

        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/content-submissions`);

        if (!response.ok) {
          console.error('Ã¢ÂÅ’ Failed to fetch previous submissions:', response.status);
          setPreviousSubmissionLoaded(true);
          return;
        }

        const data = await response.json();
        console.log('Ã°Å¸â€œÂ¦ Received submissions data:', data);

        // Handle both array response and object with submissions property
        const submissions = Array.isArray(data) ? data : (data.submissions || []);
        console.log('Ã°Å¸â€œâ€¹ Total submissions count:', submissions.length);

        if (submissions.length > 0) {
          console.log('Ã°Å¸â€Â Sample submission structure:', {
            assignment_id: submissions[0].assignment_id,
            created_by: submissions[0].created_by,
            caption: submissions[0].caption?.substring(0, 50),
            hashtags: submissions[0].hashtags?.substring(0, 50)
          });
        }

        // Filter submissions for this specific assignment
        // Primary: match by calendar_id + item_index (unambiguous)
        // Fallback: match by assignment_id title (for older submissions)
        const assignmentCreatedAt = assignment.createdAt ? new Date(assignment.createdAt).getTime() : 0;
        const previousSubmissions = submissions.filter(sub => {
          const subCalendarId = sub.calendar_id || sub.calendarId;
          const subItemIndex = (sub.item_index !== undefined && sub.item_index !== null)
            ? String(sub.item_index)
            : undefined;
          const subAssignmentId = sub.assignment_id || sub.assignmentId || sub.assignmentID;
          const subCreatedAtValue = sub.created_at || sub.createdAt;
          const subCreatedAt = subCreatedAtValue ? new Date(subCreatedAtValue).getTime() : 0;
          const isNewerThanAssignment = !assignmentCreatedAt || !subCreatedAt || subCreatedAt >= assignmentCreatedAt;

          if (!isNewerThanAssignment) {
            return false;
          }

          // Primary — most reliable: calendar_id + item_index
          if (subCalendarId && subItemIndex !== undefined) {
            return subCalendarId === calendarId && subItemIndex === String(itemIndex);
          }

          // Secondary — calendar_id + assignment_id (covers old submissions without item_index)
          if (subCalendarId && subAssignmentId && assignment.id) {
            return subCalendarId === calendarId &&
              String(subAssignmentId) === String(assignment.id) &&
              String(subAssignmentId) !== 'undefined';
          }

          // Final fallback — assignment_id only (legacy)
          return (
            subAssignmentId === assignment.id ||
            subAssignmentId === assignment._id ||
            (subAssignmentId && assignment.id &&
              String(subAssignmentId) === String(assignment.id) &&
              String(subAssignmentId) !== 'undefined')
          );
        });

        console.log('Ã¢Å“â€¦ Found', previousSubmissions.length, 'previous submissions for this assignment (any creator)');

        // Show ALL versions (internal and customer-stage) so history is always visible
        const allVersionsSorted = [...previousSubmissions].sort((a, b) =>
          new Date(a.created_at || a.createdAt || 0) - new Date(b.created_at || b.createdAt || 0)
        );

        // Derive sentToCustomer from any customer-stage submission
        const anySentToCustomer = allVersionsSorted.some(
          sub => (sub.submission_stage || sub.submissionStage || '') === 'customer'
        );
        setSentToCustomer(anySentToCustomer);

        // Detect if admin (not this creator) already uploaded and sent to customer
        const adminUploaded = allVersionsSorted.some(
          sub => (sub.submission_stage || sub.submissionStage || '') === 'customer' &&
            (sub.created_by || '') !== creatorEmail
        );
        setAdminUploadedForThis(adminUploaded);

        // Show ALL versions in history (creator should see all their submissions,
        // including ones that have been sent to the customer)
        if (allVersionsSorted.length > 0) {
          const normalized = allVersionsSorted
            .map((sub, idx) => {
              const thumbnailUrl = sub.thumbnailUrl || sub.thumbnail_url || sub.thumbnail?.url || null;
              const media = normMedia(
                sub.media || sub.images || [],
                thumbnailUrl || ''
              );
              const maxMediaIndex = thumbnailUrl ? media.length : Math.max(0, media.length - 1);
              const dedupedComments = (sub.comments || []).filter((c, i, arr) => i === arr.findIndex(x => x.id === c.id));
              const normalizedComments = dedupedComments.map(comment => {
                const rawIdx = comment?.mediaIndex ?? comment?.media_index ?? 0;
                const parsedIdx = Number(rawIdx);
                const safeIdx = Number.isFinite(parsedIdx)
                  ? Math.min(Math.max(parsedIdx, 0), maxMediaIndex)
                  : 0;

                // Admin UI appends thumbnail as the last media item; clamp that index
                // back to a real media index so creator can still see the comment.
                return {
                  ...comment,
                  mediaIndex: safeIdx,
                };
              });

              return {
                thumbnailUrl,
                versionNumber: idx + 1,
                caption: sub.caption || '',
                hashtags: sub.hashtags || '',
                notes: sub.notes || '',
                media,
                createdAt: sub.created_at || sub.createdAt || '',
                status: sub.status || 'submitted',
                submissionStage: sub.submission_stage || sub.submissionStage || 'internal',
                approvalNotes: sub.approval_notes || sub.approvalNotes || '',
                comments: normalizedComments,
                id: sub._id || sub.id || idx,
                sentToCustomerAt: sub.sent_to_customer_at || sub.sentToCustomerAt || null,
              };
            });
          setAllPreviousVersions(normalized);
          const tabVersions = normalized.filter(v =>
            activeTab === 'admin'
              ? (v.submissionStage !== 'customer' || v.sentToCustomerAt)
              : (v.submissionStage === 'customer' || v.submissionStage === '')
          );
          if (tabVersions.length > 0) {
            setSelectedVersionIndex(tabVersions.length - 1);
            setSelectedMediaIndex(0);
          }
        }

        // Reset selected version when activeTab changes
        // This is handled in a separate useEffect below


        // Pre-fill from latest submission regardless of stage
        const prefillSource = [...previousSubmissions].sort((a, b) =>
          new Date(b.created_at || b.createdAt || 0) - new Date(a.created_at || a.createdAt || 0)
        );

        if (prefillSource.length > 0) {
          const latestSubmission = prefillSource[0];
          const latestThumbnailUrl = latestSubmission.thumbnailUrl || latestSubmission.thumbnail_url || latestSubmission.thumbnail?.url || '';
          if (latestSubmission.caption) setCaption(latestSubmission.caption);
          if (latestSubmission.hashtags) setHashtags(latestSubmission.hashtags);
          if (latestSubmission.notes) setNotes(latestSubmission.notes);

          const revisionStatuses = [
            'revision_requested',
            'changes_requested',
            'changes_requested_admin',
            'changes_requested_customer_approved_admin'
          ];
          if (revisionStatuses.includes(latestSubmission.status)) {
            const mediaItems = normMedia(
              latestSubmission.media || latestSubmission.images || [],
              latestThumbnailUrl
            );
            const prepopulated = mediaItems.map((item, idx) => {
              const url = item.url;
              const type = item.type || 'image';
              const name = item.name || url.split('/').pop() || `media-${idx}`;
              return {
                id: `existing-${idx}-${Date.now()}`,
                isExisting: true,
                file: null,
                preview: url,
                publicUrl: url,
                type: type,
                name: name,
                uploaded: true,
                uploading: false,
                error: null
              };
            });
            setUploadedFiles(prepopulated);
          }
        }

        setPreviousSubmissionLoaded(true);
      } catch (err) {
        console.error('Ã¢ÂÅ’ Error fetching previous submission:', err);
        setPreviousSubmissionLoaded(true);
      }
    };

    if (assignment && creatorEmail) {
      fetchPreviousSubmission();
    }
  }, [assignment, creatorEmail, previousSubmissionLoaded]);

  // No-op: Removed reactive activeTab enforcement to allow manual version selection switching tabs.

  // Sync comments when version or media selection changes and activeTab changes
  useEffect(() => {
    if (previousVersions.length > 0 && previousVersions[selectedVersionIndex]) {
      const allComments = previousVersions[selectedVersionIndex].comments || [];
      const filteredComments = allComments.filter(c => {
        const isAdminComment = c.reviewType === 'internal' || c.authorRole === 'admin' || c.author === 'Admin';

        if (!isAdmin) {
          // Creator only sees finalized comments
          if (c.discarded) return false;
          return c.finalized === true;
        }

        return activeTab === 'admin' ? (isAdminComment && !c.discarded) : (!isAdminComment && !c.discarded);
      });
      setCommentsForVersion(filteredComments);

      // Auto mark unread replies as read for creator / admin
      if (assignment) {
        filteredComments.forEach(comment => {
          let commentUpdated = false;
          const updatedReplies = (comment.replies || []).map(rep => {
            if (isAdmin) {
              const isIncoming = rep.authorRole === 'customer' || rep.authorRole === 'creator';
              if (isIncoming && !rep.readByAdmin) {
                commentUpdated = true;
                return { ...rep, readByAdmin: true };
              }
            } else {
              const isIncoming = rep.authorRole === 'admin' || rep.authorRole === 'customer';
              if (isIncoming && !rep.readByCreator) {
                commentUpdated = true;
                return { ...rep, readByCreator: true };
              }
            }
            return rep;
          });

          if (commentUpdated) {
            fetch(
              `${process.env.REACT_APP_API_URL}/api/content-submissions/${encodeURIComponent(assignment.id)}/comments/${comment.id}`,
              {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ replies: updatedReplies }),
              }
            ).catch(err => console.error('Auto mark read failed:', err));
          }
        });
      }
    } else {
      setCommentsForVersion([]);
    }
    setActiveVersionComment(null);
  }, [previousVersions, selectedVersionIndex, activeTab, isAdmin, assignment]);

  useEffect(() => {
    const filtered = commentsForVersion.filter(c => {
      const idx = Number(c.mediaIndex !== undefined ? c.mediaIndex : 0);
      return idx === selectedMediaIndex;
    });
    setCommentsForCurrentMedia(filtered);
  }, [commentsForVersion, selectedMediaIndex]);

  useEffect(() => {
    setVersionImgDimensions(null);
  }, [selectedVersionIndex, selectedMediaIndex]);

  // Ã¢â€â‚¬Ã¢â€â‚¬ Version history helpers Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬
  const getVersionStatusColor = (status) => {
    switch (status) {
      case 'under_review': case 'submitted': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'approved': return 'bg-green-100 text-green-800 border-green-200';
      case 'published': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'revision_requested': return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'rejected': return 'bg-red-100 text-red-800 border-red-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const formatVersionDateLong = (dateString) => {
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        month: 'long', day: 'numeric', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
      });
    } catch { return 'Unknown date'; }
  };

  const handleVersionSelect = (index) => {
    setSelectedVersionIndex(index);
    setSelectedMediaIndex(0);
  };

  const handleVersionMediaChange = (direction) => {
    const current = previousVersions[selectedVersionIndex];
    if (!current) return;
    const mediaLength = current.media.length;
    if (direction === 'prev' && selectedMediaIndex > 0) setSelectedMediaIndex(selectedMediaIndex - 1);
    else if (direction === 'next' && selectedMediaIndex < mediaLength - 1) setSelectedMediaIndex(selectedMediaIndex + 1);
  };

  const handleVersionCommentClick = (id) => {
    setActiveVersionComment(activeVersionComment === id ? null : id);
    const comment = commentsForCurrentMedia.find(c => c.id === id);
    if (comment && comment.videoTimestamp != null && videoRef.current) {
      videoRef.current.currentTime = comment.videoTimestamp;
      videoRef.current.pause();
      videoRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  };

  const handleVersionToggleDone = async (id) => {
    const comment = commentsForCurrentMedia.find(c => c.id === id);
    if (comment && assignment) {
      const newDone = !comment.done;
      try {
        await fetch(
          `${process.env.REACT_APP_API_URL}/api/content-submissions/${encodeURIComponent(assignment.id)}/comments/${comment.id}`,
          { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ done: newDone, status: newDone ? 'completed' : 'pending' }) }
        );
      } catch (err) { }
      setCommentsForVersion(commentsForVersion.map(c => c.id === id ? { ...c, done: newDone } : c));
      setCommentsForCurrentMedia(commentsForCurrentMedia.map(c => c.id === id ? { ...c, done: newDone } : c));
      if (newDone) setActiveVersionComment(null);
    }
  };

  const handleVersionReplySubmit = async (commentId) => {
    if (!replyText.trim() || !assignment) return;
    const comment = commentsForCurrentMedia.find(c => c.id === commentId);
    if (!comment) return;
    setReplySubmitting(true);
    const newReply = {
      id: uuidv4(),
      authorRole: isAdmin ? 'admin' : 'creator',
      authorName: isAdmin ? (currentUser.name || 'Admin') : 'Creator',
      authorEmail: currentUserEmail || '',
      message: replyText.trim(),
      timestamp: new Date().toISOString(),
    };
    const existingReplies = comment.replies || (comment.reply ? [{
      id: 'legacy-creator-reply',
      authorRole: 'creator',
      authorName: 'Creator',
      authorEmail: comment.reply.creatorEmail || '',
      message: comment.reply.text,
      timestamp: comment.reply.timestamp
    }] : []);

    let updatedReplies = [...existingReplies];

    if (editingReplyId) {
      const index = updatedReplies.findIndex(r => r.id === editingReplyId);
      if (index !== -1) {
        updatedReplies[index] = {
          ...updatedReplies[index],
          message: replyText.trim(),
          timestamp: new Date().toISOString()
        };
      }
    } else {
      updatedReplies.push(newReply);
    }

    const replyPayload = {
      reply: {
        text: replyText.trim(),
        creatorEmail: currentUserEmail,
        timestamp: new Date().toISOString(),
      },
      replies: updatedReplies,
    };
    try {
      await fetch(
        `${process.env.REACT_APP_API_URL}/api/content-submissions/${encodeURIComponent(assignment.id)}/comments/${commentId}`,
        { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(replyPayload) }
      );
      const updated = { ...comment, ...replyPayload };
      setCommentsForVersion(commentsForVersion.map(c => c.id === commentId ? updated : c));
      setCommentsForCurrentMedia(commentsForCurrentMedia.map(c => c.id === commentId ? updated : c));
      setReplyingToComment(null);
      setEditingReplyId(null);
      setReplyText('');
    } catch (err) {
      console.error('Failed to save reply:', err);
    } finally {
      setReplySubmitting(false);
    }
  };

  const handleMarkReplyRead = async (commentId, replyId) => {
    const comment = commentsForVersion.find(c => c.id === commentId);
    if (!comment || !assignment) return;

    const updatedReplies = (comment.replies || []).map(rep => {
      if (rep.id === replyId) {
        return { ...rep, readByCreator: true };
      }
      return rep;
    });

    const replyPayload = {
      replies: updatedReplies,
    };
    try {
      await fetch(
        `${process.env.REACT_APP_API_URL}/api/content-submissions/${encodeURIComponent(assignment.id)}/comments/${commentId}`,
        { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(replyPayload) }
      );
      const updated = { ...comment, ...replyPayload };
      setCommentsForVersion(commentsForVersion.map(c => c.id === commentId ? updated : c));
      setCommentsForCurrentMedia(commentsForCurrentMedia.map(c => c.id === commentId ? updated : c));
    } catch (err) {
      console.error('Failed to mark reply read:', err);
    }
  };

  const isCreatorOwnedEntry = (entry) => {
    if (!entry) return false;
    const role = String(entry.authorRole || '').toLowerCase();
    const entryEmail = String(entry.authorEmail || entry.creatorEmail || '').toLowerCase();
    if (role === 'creator') return true;
    return Boolean(creatorEmail && entryEmail && entryEmail === creatorEmail);
  };

  const isCurrentAdminOwnEntry = (entry) => {
    if (!entry || !isAdmin) return false;
    const role = String(entry.authorRole || '').toLowerCase();
    if (role !== 'admin') return false;

    const entryEmail = String(entry.authorEmail || entry.creatorEmail || '').toLowerCase();
    if (entryEmail && currentUserEmail) return entryEmail === currentUserEmail;

    const entryName = String(entry.authorName || '').toLowerCase();
    if (entryName && currentUser.name) return entryName === currentUser.name;

    return false;
  };

  const canReplyToEntry = (entry) => {
    if (!entry) return false;
    return true;
  };

  const isOwnReply = (rep) => {
    if (!rep) return false;
    const role = rep.authorRole;
    if (isAdmin) {
      return role === 'admin';
    } else {
      return role === 'creator';
    }
  };

  const handleVersionCommentDelete = async (commentId) => {
    if (!assignment || !commentId) return;
    if (!window.confirm('Delete this comment? This action cannot be undone.')) return;

    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/api/content-submissions/${encodeURIComponent(assignment.id)}/comments/${commentId}`,
        { method: 'DELETE' }
      );
      if (!res.ok) throw new Error('Failed to delete comment');

      setCommentsForVersion(prev => prev.filter(c => c.id !== commentId));
      setCommentsForCurrentMedia(prev => prev.filter(c => c.id !== commentId));

      if (activeVersionComment === commentId) {
        setActiveVersionComment(null);
      }
      if (replyingToComment === commentId) {
        setReplyingToComment(null);
        setEditingReplyId(null);
        setReplyText('');
      }
    } catch (err) {
      console.error('Failed to delete comment:', err);
      alert('Failed to delete comment. Please try again.');
    }
  };

  const handleVersionReplyDelete = async (commentId, replyId) => {
    if (!assignment || !commentId || !replyId) return;
    if (!window.confirm('Delete this reply? This action cannot be undone.')) return;

    const parentComment = commentsForCurrentMedia.find(c => c.id === commentId);
    if (!parentComment) return;

    const existingReplies = parentComment.replies || (parentComment.reply ? [{
      id: 'legacy-creator-reply',
      authorRole: 'creator',
      authorName: parentComment.reply.creatorName || 'Creator',
      authorEmail: parentComment.reply.creatorEmail || '',
      message: parentComment.reply.text,
      timestamp: parentComment.reply.timestamp
    }] : []);

    const updatedReplies = existingReplies.filter(r => r.id !== replyId);
    const latestCreatorReply = [...updatedReplies].reverse().find(r => String(r.authorRole || '').toLowerCase() === 'creator');

    const updatePayload = {
      replies: updatedReplies,
      reply: latestCreatorReply ? {
        text: latestCreatorReply.message || latestCreatorReply.text || '',
        creatorEmail: latestCreatorReply.authorEmail || creatorEmail || '',
        timestamp: latestCreatorReply.timestamp || new Date().toISOString(),
      } : null,
    };

    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/api/content-submissions/${encodeURIComponent(assignment.id)}/comments/${commentId}`,
        { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(updatePayload) }
      );
      if (!res.ok) throw new Error('Failed to delete reply');

      const nextComment = { ...parentComment, ...updatePayload };
      setCommentsForVersion(prev => prev.map(c => c.id === commentId ? nextComment : c));
      setCommentsForCurrentMedia(prev => prev.map(c => c.id === commentId ? nextComment : c));

      if (editingReplyId === replyId) {
        setEditingReplyId(null);
        setReplyText('');
      }
    } catch (err) {
      console.error('Failed to delete reply:', err);
      alert('Failed to delete reply. Please try again.');
    }
  };

  const handleVersionImgLoad = (e) => {
    const el = e.target;
    const ew = el.clientWidth, eh = el.clientHeight;
    const nw = el.naturalWidth || ew;
    const nh = el.naturalHeight || eh;
    const scale = Math.min(ew / nw, eh / nh);
    const contentW = nw * scale, contentH = nh * scale;
    setVersionImgDimensions({ contentW, contentH, offsetX: (ew - contentW) / 2, offsetY: (eh - contentH) / 2 });
  };

  const getVersionScaledMarkerPos = (commentX, commentY) => {
    const { contentW = 0, contentH = 0, offsetX = 0, offsetY = 0 } = versionImgDimensions || {};
    if (commentX <= 1 && commentY <= 1 && contentW > 0) {
      return { x: commentX * contentW + offsetX, y: commentY * contentH + offsetY };
    }
    if (contentW > 0) {
      const s = contentW / 680;
      return { x: commentX * s + offsetX, y: commentY * s + offsetY };
    }
    return { x: commentX, y: commentY };
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFiles(e.dataTransfer.files);
    }
  };

  const handleChange = (e) => {
    e.preventDefault();
    if (e.target.files && e.target.files[0]) {
      handleFiles(e.target.files);
    }
  };

  const handleThumbnailChange = async (e) => {
    e.preventDefault();
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      if (!file.type.startsWith('image/')) {
        alert('Please select an image file for the thumbnail.');
        return;
      }

      const validationPlatforms = getValidationPlatforms(assignment?.platform);
      const validationResult = await validateThumbnail(file, {
        platforms: validationPlatforms,
      });

      const validationIssues = validationResult.issues || [];
      const errorIssue = validationIssues.find(issue => issue.level === 'error') || null;
      const warningIssues = validationIssues.filter(issue => issue.level === 'warning');

      if (validationIssues.length > 0) {
        setValidationNotice(validationIssues.map(issue => issue.message).join(' '));
        if (errorIssue) {
          window.alert(errorIssue.message);
          return;
        } else {
          window.alert(validationIssues.map(issue => issue.message).join(' '));
        }
      } else {
        setValidationNotice('');
      }

      const preview = URL.createObjectURL(file);
      setThumbnailFileObj({
        file,
        preview,
        name: file.name,
        size: file.size,
        uploaded: false,
        uploading: false,
        publicUrl: null,
        error: errorIssue ? errorIssue.message : null,
        warnings: warningIssues.map(issue => issue.message),
        validationIssues
      });
    }
  };

  const onThumbnailButtonClick = () => {
    thumbnailInputRef.current?.click();
  };

  const handleFiles = async (files) => {
    const incomingFiles = Array.from(files || []);
    const validationPlatforms = getValidationPlatforms(assignment?.platform);

    const postType = (assignment?.postType || '').toLowerCase();
    const selectionError = validateMediaSelectionForPostType([...uploadedFiles, ...incomingFiles], postType);
    if (selectionError) {
      window.alert(selectionError);
      return;
    }

    for (const file of incomingFiles) {
      if (!file.type.startsWith('image/') && !file.type.startsWith('video/')) {
        continue;
      }

      const validationResult = await validateMediaForPlatforms(file, {
        platforms: validationPlatforms,
      });

      const preview = URL.createObjectURL(file);
      const validationIssues = validationResult.issues || [];
      const errorIssue = validationIssues.find(issue => issue.level === 'error') || null;
      const warningIssues = validationIssues.filter(issue => issue.level === 'warning');

      const newFile = {
        id: Date.now() + Math.random(),
        file,
        preview,
        name: file.name,
        size: file.size,
        type: file.type.startsWith('image/') ? 'image' : 'video',
        uploaded: false,
        uploading: false,
        publicUrl: null,
        error: errorIssue ? errorIssue.message : null,
        warnings: warningIssues.map(issue => issue.message),
        validationIssues,
      };

      setUploadedFiles(prev => [...prev, newFile]);

      if (validationIssues.length > 0) {
        setValidationNotice(validationIssues.map(issue => issue.message).join(' '));
        if (errorIssue) {
          window.alert(errorIssue.message);
        }
      } else {
        setValidationNotice('');
      }
    }
  };

  const removeFile = (fileId) => {
    setUploadedFiles(prev => prev.filter(file => file.id !== fileId));
  };

  const formatFileSize = (bytes) => {
    if (!bytes || typeof bytes !== 'number' || bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleReplaceClick = (fileId) => {
    setReplacingFileId(fileId);
    replaceFileInputRef.current?.click();
  };

  const handleReplaceChange = async (e) => {
    e.preventDefault();
    if (e.target.files && e.target.files[0] && replacingFileId !== null) {
      const file = e.target.files[0];
      await handleReplaceFile(replacingFileId, file);
      e.target.value = '';
      setReplacingFileId(null);
    }
  };

  const handleReplaceFile = async (fileId, file) => {
    if (!file.type.startsWith('image/') && !file.type.startsWith('video/')) {
      window.alert('Please select an image or video file.');
      return;
    }

    const validationPlatforms = getValidationPlatforms(assignment?.platform);
    const postType = (assignment?.postType || '').toLowerCase();

    const nextUploadedFiles = uploadedFiles.map(existingFile => {
      if (existingFile.id === fileId) {
        return {
          ...existingFile,
          file,
          type: file.type.startsWith('image/') ? 'image' : 'video'
        };
      }
      return existingFile;
    });
    const selectionError = validateMediaSelectionForPostType(nextUploadedFiles, postType);
    if (selectionError) {
      window.alert(selectionError);
      return;
    }

    const validationResult = await validateMediaForPlatforms(file, {
      platforms: validationPlatforms,
    });

    const preview = URL.createObjectURL(file);
    const validationIssues = validationResult.issues || [];
    const errorIssue = validationIssues.find(issue => issue.level === 'error') || null;
    const warningIssues = validationIssues.filter(issue => issue.level === 'warning');

    if (validationIssues.length > 0) {
      setValidationNotice(validationIssues.map(issue => issue.message).join(' '));
      if (errorIssue) {
        window.alert(errorIssue.message);
      }
    } else {
      setValidationNotice('');
    }

    setUploadedFiles(prev => prev.map(f => {
      if (f.id === fileId) {
        return {
          id: f.id,
          file,
          preview,
          name: file.name,
          size: file.size,
          type: file.type.startsWith('image/') ? 'image' : 'video',
          isExisting: false,
          isReplaced: true,
          uploaded: false,
          uploading: false,
          publicUrl: null,
          error: errorIssue ? errorIssue.message : null,
          warnings: warningIssues.map(issue => issue.message),
          validationIssues,
        };
      }
      return f;
    }));
  };

  // Upload single file - uses streaming upload for large files (>=10MB), base64 for small files
  const uploadFileToGCS = async (fileObj) => {
    try {
      const fileSizeMB = fileObj.file.size / (1024 * 1024);
      console.log(`Ã°Å¸â€œÂ¤ Starting upload for ${fileObj.name} (${formatFileSize(fileObj.size)})`);
      // Validate file object
      if (!fileObj.file || !fileObj.file.size || fileObj.file.size === 0) {
        throw new Error(`Invalid file: ${fileObj.name} has no content or is corrupted`);
      }

      // Update file status to uploading
      setUploadedFiles(prev =>
        prev.map(f => f.id === fileObj.id ? { ...f, uploading: true, error: null } : f)
      );

      let publicUrl;

      // Use pre-signed GCS URL upload for all files (prevents FileReader errors on mobile)
      console.log(`📦 Using signed-URL upload for file: ${fileObj.name} (${fileSizeMB.toFixed(2)} MB)`);

      // Step 1: Get a signed URL from the backend
      const signedUrlResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/gcs/signed-url`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ filename: fileObj.name, contentType: fileObj.file.type }),
      });

      if (!signedUrlResponse.ok) {
        const errorData = await signedUrlResponse.json().catch(() => ({}));
        throw new Error(`Failed to get signed URL: ${errorData.error || signedUrlResponse.statusText}`);
      }

      const { signedUrl, publicUrl: signedPublicUrl } = await signedUrlResponse.json();

      // Step 2: PUT the file directly to GCS using the signed URL
      const gcsUploadResponse = await fetch(signedUrl, {
        method: 'PUT',
        headers: { 'Content-Type': fileObj.file.type },
        body: fileObj.file,
      });

      if (!gcsUploadResponse.ok) {
        throw new Error(`GCS upload failed: ${gcsUploadResponse.statusText}`);
      }

      publicUrl = signedPublicUrl;
      console.log(`✅ Successfully uploaded ${fileObj.name} via signed URL`);

      if (!publicUrl) {
        throw new Error('No public URL returned from upload');
      }

      // Update file status to uploaded
      setUploadedFiles(prev =>
        prev.map(f => f.id === fileObj.id ? {
          ...f,
          uploading: false,
          uploaded: true,
          publicUrl: publicUrl,
          error: null
        } : f)
      );

      return {
        url: publicUrl,
        type: fileObj.type,
        name: fileObj.name,
        size: fileObj.file.size,
        originalName: fileObj.name
      };

    } catch (error) {
      console.error(`Ã¢ÂÅ’ Upload failed for ${fileObj.name}:`, error);

      // Update file status to error
      setUploadedFiles(prev =>
        prev.map(f => f.id === fileObj.id ? {
          ...f,
          uploading: false,
          uploaded: false,
          error: error.message
        } : f)
      );

      throw error;
    }
  };

  const uploadThumbnailToGCS = async (fileObj) => {
    try {
      setThumbnailFileObj(prev => prev ? { ...prev, uploading: true, error: null } : null);

      let publicUrl;
      const fileSizeMB = fileObj.file.size / (1024 * 1024);

      console.log(`📦 Using signed-URL upload for thumbnail: ${fileObj.name} (${fileSizeMB.toFixed(2)} MB)`);

      const signedUrlResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/gcs/signed-url`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ filename: fileObj.name, contentType: fileObj.file.type }),
      });
      if (!signedUrlResponse.ok) throw new Error('Failed to get signed URL');
      const { signedUrl, publicUrl: signedPublicUrl } = await signedUrlResponse.json();

      const gcsUploadResponse = await fetch(signedUrl, {
        method: 'PUT',
        headers: { 'Content-Type': fileObj.file.type },
        body: fileObj.file,
      });
      if (!gcsUploadResponse.ok) throw new Error('GCS upload failed');
      publicUrl = signedPublicUrl;

      setThumbnailFileObj(prev => prev ? { ...prev, uploading: false, uploaded: true, publicUrl } : null);
      return { url: publicUrl };
    } catch (error) {
      console.error('Thumbnail upload failed:', error);
      setThumbnailFileObj(prev => prev ? { ...prev, uploading: false, uploaded: false, error: error.message } : null);
      throw error;
    }
  };

  const handleSubmit = async () => {
    if (uploadedFiles.length === 0) {
      alert('Please upload at least one image or video');
      return;
    }

    const postType = (assignment?.postType || '').toLowerCase();
    const selectionError = validateMediaSelectionForPostType(uploadedFiles, postType);
    if (selectionError) {
      alert(selectionError);
      return;
    }

    if (hasMixedContent(uploadedFiles)) {
      alert('Mixed content is not allowed for carousel posts. You cannot upload images and videos together.');
      return;
    }

    if (hasMultipleVideos(uploadedFiles)) {
      alert('Multiple videos are not allowed. A carousel post can only contain a single video or multiple images.');
      return;
    }

    if (!assignment) {
      alert('Assignment data not found. Please try again.');
      return;
    }

    // Enhanced validation and fallback for customer information
    let finalCustomerId = assignment.customerId;
    let finalCustomerName = assignment.customerName;
    let finalCustomerEmail = assignment.customerEmail;

    if (!finalCustomerId || !finalCustomerName) {
      console.error('Ã¢ÂÅ’ Missing customer information in assignment');

      // Use calendar data as fallback
      finalCustomerId = finalCustomerId || assignment.calendarId || '';
      finalCustomerName = finalCustomerName || assignment.calendarName || 'Unknown Customer';
    }

    // Additional validation to ensure we have proper customer name
    if (!finalCustomerName || finalCustomerName === 'Unknown Customer') {
      finalCustomerName = finalCustomerName ||
        assignment.customer ||
        assignment.client ||
        assignment.calendarName ||
        'Unknown Customer';
    }

    // CRITICAL: Ensure we have valid customer information before proceeding
    if (!finalCustomerId || !finalCustomerName || finalCustomerName === 'Unknown Customer') {
      console.error('Ã¢Â Å’ CRITICAL: Cannot proceed without valid customer information!');
      alert(`Missing customer information. Please contact support.\n\nDetails:\n- Customer ID: ${finalCustomerId || 'Missing'}\n- Customer Name: ${finalCustomerName || 'Missing'}\n- Assignment ID: ${assignment.id}`);
      return;
    }

    // Inherit thumbnail from previous version if not modified/removed
    let previousThumbnailUrl = null;
    if (allPreviousVersions && allPreviousVersions.length > 0) {
      const latestPrev = allPreviousVersions[allPreviousVersions.length - 1];
      previousThumbnailUrl = latestPrev.thumbnailUrl || null;
    }

    setSubmitting(true);
    const uploadedMediaUrls = [];
    let uploadedThumbnailUrl = null;

    try {
      // Upload thumbnail if present and not uploaded yet
      if (thumbnailFileObj) {
        if (!thumbnailFileObj.uploaded) {
          console.log(`Uploading thumbnail file: ${thumbnailFileObj.name}`);
          const thumbRes = await uploadThumbnailToGCS(thumbnailFileObj);
          uploadedThumbnailUrl = thumbRes.url;
        } else {
          uploadedThumbnailUrl = thumbnailFileObj.publicUrl;
        }
      } else if (!thumbnailFileRemoved && previousThumbnailUrl) {
        uploadedThumbnailUrl = previousThumbnailUrl;
      }

      // Upload new files and keep existing ones, preserving exact carousel order
      const uploadPromises = uploadedFiles.map(async (fileObj) => {
        if ((fileObj.isExisting || fileObj.uploaded) && fileObj.publicUrl) {
          return {
            url: fileObj.publicUrl,
            type: fileObj.type,
            name: fileObj.name,
            size: fileObj.size,
            originalName: fileObj.name
          };
        } else {
          return await uploadFileToGCS(fileObj);
        }
      });

      console.log(`Ã°Å¸â€œÂ¤ Processing ${uploadedFiles.length} files (uploading new/replaced files, keeping existing)...`);
      const results = await Promise.all(uploadPromises);
      uploadedMediaUrls.push(...results);

      console.log(`Ã¢Å“â€¦ All files processed successfully. Total: ${uploadedMediaUrls.length}`);

      // Prepare submission data with comprehensive customer information
      const submissionData = {
        assignment_id: assignment.id,
        caption: caption || '',
        hashtags: hashtags || '',
        notes: notes || '',
        images: uploadedMediaUrls, // Ã¢Å“â€¦ This contains the media URLs and metadata
        media: uploadedMediaUrls,  // Ã¢Å“â€¦ Duplicate for backward compatibility
        thumbnailUrl: uploadedThumbnailUrl || null, // Ã¢Å“â€¦ Include video thumbnail url
        created_by: creatorEmail,
        customer_id: finalCustomerId,
        customer_name: finalCustomerName,
        customer_email: finalCustomerEmail || assignment.customerEmail || '',
        platform: assignment.platform || 'Instagram',
        calendar_id: assignment.calendarId,
        calendar_name: assignment.calendarName,
        item_name: assignment.title || '',
        item_id: assignment.stableItemId || assignment.id || '',
        assignment_title: assignment.title,
        assignment_description: assignment.description,
        due_date: assignment.dueDate,
        status: 'submitted',
        // Content creators submit for internal review; they can no longer upload directly for customer review
        submission_stage: 'internal',
        item_index: assignment.itemIndex !== undefined ? Number(assignment.itemIndex) : parseInt(itemIndex, 10),
        created_at: new Date().toISOString(),
        type: 'submission',
        geo_location: (geoLocation.latitude && geoLocation.longitude) ? geoLocation : undefined,
        address: address || undefined,
        contact_info: contactInfo || undefined,
        notify_admins: displayAdminsList,
      };

      // FINAL VALIDATION - Ensure all critical fields are present
      const missingFields = [];
      if (!submissionData.customer_id) missingFields.push('customer_id');
      if (!submissionData.customer_name) missingFields.push('customer_name');
      if (!submissionData.assignment_id) missingFields.push('assignment_id');
      if (!submissionData.created_by) missingFields.push('created_by');
      if (!submissionData.images || submissionData.images.length === 0) missingFields.push('images');

      if (missingFields.length > 0) {
        console.error('Ã¢ÂÅ’ VALIDATION FAILED: Missing required fields:', missingFields);
        alert(`Validation failed. Missing required fields: ${missingFields.join(', ')}`);
        setSubmitting(false);
        return;
      }

      // Additional validation for customer info quality
      if (submissionData.customer_name === 'Unknown Customer' ||
        submissionData.customer_name.length < 2 ||
        !submissionData.customer_id ||
        submissionData.customer_id.length < 5) {
        console.error('Ã¢ÂÅ’ QUALITY CHECK FAILED: Invalid customer information quality');
        alert('Invalid customer information detected. Please refresh the page and try again.');
        setSubmitting(false);
        return;
      }

      console.log('Ã°Å¸â€œÂ¤ Submitting content to API...');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/content-submissions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(submissionData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error('Ã¢ÂÅ’ Backend submission failed:', errorData);
        throw new Error(`Backend error: ${errorData.error || 'Unknown error'}`);
      }

      const result = await response.json();
      console.log('Ã¢Å“â€¦ Content submission successful:', result);

      if (activeTab === 'admin') {
        const adminNames = displayAdminsList.map(a => a.name).join(', ');
        const adminMsg = displayAdminsList.length > 0
          ? `Notified admin(s): ${adminNames}.`
          : 'System admins will review this submission.';
        setSubmitSuccessModal({
          open: true,
          title: 'Submitted for Admin Review',
          message: `${adminMsg} Content will move to customer review after approval.`
        });
      } else {
        setSubmitSuccessModal({
          open: true,
          title: 'Submitted for Customer Review',
          message: 'Your content has been sent directly to the customer for review.'
        });
      }
    } catch (err) {
      console.error('Ã¢ÂÅ’ Upload error:', err);
      alert(`Upload failed: ${err.message}. Please try again.`);
    } finally {
      setSubmitting(false);
    }
  };

  const onButtonClick = () => {
    if (!canAddMoreMedia) return;
    fileInputRef.current?.click();
  };

  // Send approved internal submission to customer for review
  const handleSendToCustomer = async () => {
    const latestApproved = [...previousVersions].reverse().find(v => v.status === 'approved');
    if (!latestApproved) return;
    setSendingToCustomer(true);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/api/content-submissions/${encodeURIComponent(latestApproved.id)}/send-to-customer`,
        { method: 'PATCH', headers: { 'Content-Type': 'application/json' } }
      );
      if (!res.ok) throw new Error('Failed');
      setSentToCustomer(true);
    } catch {
      alert('Failed to send to customer. Please try again.');
    } finally {
      setSendingToCustomer(false);
    }
  };

  // Retry upload for failed files
  const retryUpload = async (fileObj) => {
    try {
      await uploadFileToGCS(fileObj);
    } catch (error) {
      console.error('Retry failed:', error);
    }
  };

  const closeSubmitSuccessModal = () => {
    setSubmitSuccessModal({ open: false, title: '', message: '' });
    if (isAdmin) {
      window.history.back();
      return;
    }
    if (assignment && assignment.customerId) {
      navigate(`/content-creator/assignments?expand=${assignment.customerId}`);
      return;
    }
    navigate('/content-creator/assignments');
  };

  // No params — show assignment picker
  if (!calendarId || itemIndex === undefined) {
    // Derived filter options
    const allPlatforms = [...new Set(pickerAssignments.flatMap(a => flatPlatforms(a.platform).map(p => p.charAt(0).toUpperCase() + p.slice(1))))].sort();
    const allStatuses = [...new Set(pickerAssignments.map(a => getPickerFilterStatus(a)))].sort();
    const allCustomers = [...new Set(pickerAssignments.map(a => a.customerName || a.calendarName || 'Unknown'))].sort();

    const filtered = pickerAssignments
      .filter(a => {
        const q = pickerSearch.toLowerCase();
        const platforms = flatPlatforms(a.platform);
        const matchSearch = !q ||
          (a.title || '').toLowerCase().includes(q) ||
          (a.customerName || '').toLowerCase().includes(q) ||
          platforms.some(p => p.includes(q)) ||
          (a.description || '').toLowerCase().includes(q);
        const matchPlatform = pickerPlatform === 'all' || platforms.some(p => p === pickerPlatform.toLowerCase());

        let matchStatus = false;
        if (pickerStatus === 'all') {
          matchStatus = true;
        } else if (pickerStatus === 'admin_approved') {
          const subs = pickerSubmissions.filter(sub => {
            const subCal = sub.calendar_id || sub.calendarId;
            const subIdx = sub.item_index !== undefined ? String(sub.item_index) : undefined;
            if (subCal && subIdx !== undefined) {
              return subCal === a.calendarId && subIdx === String(a.itemIndex);
            }
            const subId = sub.assignment_id || sub.assignmentId;
            return subId && String(subId) === String(a.id);
          });
          matchStatus = subs.some(s => s.status === 'approved' && (s.submission_stage || s.submissionStage || 'internal') !== 'customer');
        } else {
          matchStatus = getPickerFilterStatus(a) === pickerStatus;
        }

        const matchCustomer = pickerCustomer === 'all' || a.customerId === pickerCustomer;
        return matchSearch && matchPlatform && matchStatus && matchCustomer;
      })
      .sort((a, b) => {
        if (pickerSort === 'due') {
          const da = new Date(a.assignedAt || a.createdAt || a.dueDate || 0);
          const db = new Date(b.assignedAt || b.createdAt || b.dueDate || 0);
          return db - da;
        }
        if (pickerSort === 'name') return (a.title || '').localeCompare(b.title || '');
        if (pickerSort === 'customer') return (a.customerName || '').localeCompare(b.customerName || '');
        return 0;
      });

    const platformColor = (p) => {
      switch ((p || '').toLowerCase()) {
        case 'facebook': return 'bg-blue-100 text-blue-700 border-blue-200';
        case 'instagram': return 'bg-pink-100 text-pink-700 border-pink-200';
        case 'youtube': return 'bg-red-100 text-red-700 border-red-200';
        case 'linkedin': return 'bg-blue-50 text-blue-700 border-blue-200';
        case 'twitter': return 'bg-sky-100 text-sky-700 border-sky-200';
        case 'tiktok': return 'bg-gray-900 text-white border-gray-700';
        default: return 'bg-gray-100 text-gray-700 border-gray-200';
      }
    };

    const PlatformIcon = ({ platform, className = 'h-3 w-3' }) => {
      switch ((platform || '').toLowerCase()) {
        case 'facebook': return <Facebook className={className} />;
        case 'instagram': return <Instagram className={className} />;
        case 'linkedin': return <Linkedin className={className} />;
        case 'youtube': return <Youtube className={className} />;
        case 'twitter': return <Twitter className={className} />;
        default: return <Globe className={className} />;
      }
    };

    const parsePlatforms = (val) => {
      if (!val) return [];
      if (Array.isArray(val)) return val.map(v => String(v).trim()).filter(Boolean);
      const s = String(val || '');
      if (s.includes(',')) return s.split(',').map(v => v.trim()).filter(Boolean);
      if (s.includes(' ')) return s.split(/\s+/).map(v => v.trim()).filter(Boolean);
      const matches = s.match(/facebook|instagram|youtube|linkedin|twitter|tiktok|pinterest/ig);
      if (matches) return matches.map(m => m.toLowerCase());
      return [s];
    };

    const statusColor = (s) => {
      switch (s) {
        case 'approved': return 'bg-green-100 text-green-800';
        case 'published': return 'bg-purple-100 text-purple-800';
        case 'in_progress': return 'bg-amber-100 text-amber-800';
        case 'pending': return 'bg-orange-100 text-orange-800';
        default: return 'bg-gray-100 text-gray-700';
      }
    };

    const statusLabel = (s) => (s || 'pending').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

    const pickerHeaderActions = (
      <span className="text-xs font-semibold text-purple-700 bg-purple-100 px-3 py-1.5 rounded-full">
        {pickerAssignments.length} assignments
      </span>
    );

    return (
      <ContentCreatorLayout
        title="Upload Content"
        subtitle="Select an assignment to upload media for"
        icon={<Palette className="h-5 w-5 text-white" />}
        fullWidthContent={true}
        headerActions={pickerHeaderActions}
      >

        {pickerLoading ? (
          <div className="flex-1 flex items-center justify-center py-20">
            <div className="text-center">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-purple-600 mx-auto mb-3"></div>
              <span className="text-gray-600">Loading your assignments...</span>
            </div>
          </div>
        ) : pickerAssignments.length === 0 ? (
          <div className="flex-1 flex items-center justify-center p-8">
            <div className="bg-white rounded-xl shadow-sm p-10 text-center max-w-sm">
              <FileText className="h-12 w-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500 font-medium">No assignments found</p>
              <p className="text-sm text-gray-400 mt-1">You have no active assignments to upload content for.</p>
              <button onClick={() => navigate('/content-creator')} className="mt-4 px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors text-sm">
                Back to Dashboard
              </button>
            </div>
          </div>
        ) : (
          <div className="flex flex-1 overflow-hidden h-[calc(100vh-64px)]">
            {/* Ã¢â€â‚¬Ã¢â€â‚¬ MAIN LIST Ã¢â€â‚¬Ã¢â€â‚¬ */}
            <main className="flex-1 overflow-y-auto p-4 sm:p-6">
              {/* banner */}
              {selectedCustomer && (
                <div className="bg-gradient-to-r from-purple-600 via-purple-500 to-indigo-600 rounded-2xl shadow-lg p-6 text-white mb-5 flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-16 rounded-full bg-white/20 backdrop-blur-sm border border-white/30 flex items-center justify-center text-2xl font-bold overflow-hidden">
                      {selectedCustomer.profileImage ? (
                        <img src={selectedCustomer.profileImage} alt="" className="w-full h-full object-cover" />
                      ) : (
                        (selectedCustomer.name || 'C').charAt(0).toUpperCase()
                      )}
                    </div>
                    <div>
                      <h1 className="text-xl sm:text-2xl font-bold">{selectedCustomer.name || 'Unnamed Customer'}</h1>
                      <p className="text-purple-100 text-sm">{selectedCustomer.email}</p>
                      {selectedCustomer.companyName && (
                        <p className="text-purple-200 text-xs mt-1 bg-white/10 px-2 py-0.5 rounded-full inline-block">
                          {selectedCustomer.companyName}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Filter Tabs + Search */}
              <div className="bg-white/70 backdrop-blur-sm rounded-2xl shadow-sm p-4 border border-gray-200/50 mb-4">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div className="flex flex-wrap gap-2">
                    {[
                      { key: 'all', label: 'All', count: pickerStats.total },
                      { key: 'pending', label: 'Pending', count: pickerStats.pending },
                      { key: 'approved', label: 'Customer Approved', count: pickerStats.approved },
                      { key: 'published', label: 'Published', count: pickerStats.published },
                      { key: 'admin_approved', label: 'Admin Approved', count: pickerStats.adminApproved },
                    ].map(opt => (
                      <button
                        key={opt.key}
                        onClick={() => setPickerStatus(opt.key)}
                        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${pickerStatus === opt.key
                          ? 'bg-purple-600 text-white shadow-sm'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                          }`}
                      >
                        {opt.label}
                        <span className={`text-xs px-1.5 py-0.5 rounded-full font-semibold ${pickerStatus === opt.key ? 'bg-white/20 text-white' : 'bg-white text-gray-600'
                          }`}>{opt.count}</span>
                      </button>
                    ))}
                  </div>
                  <div className="relative flex-shrink-0 w-full sm:w-56">
                    <Search className="h-4 w-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                    <input
                      type="text"
                      placeholder="Search assignments..."
                      value={pickerSearch}
                      onChange={e => setPickerSearch(e.target.value)}
                      className="pl-9 pr-4 py-2 bg-white border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent shadow-sm w-full"
                    />
                  </div>
                </div>
              </div>

              {/* Active filters strip */}
              {(pickerPlatform !== 'all' || pickerStatus !== 'all' || pickerCustomer !== 'all' || pickerSearch) && (
                <div className="flex items-center gap-2 mb-4 flex-wrap">
                  <span className="text-xs text-gray-500 font-medium">Active Filters:</span>
                  {pickerPlatform !== 'all' && (
                    <button onClick={() => setPickerPlatform('all')} className="inline-flex items-center gap-1 px-2.5 py-0.5 bg-blue-100 text-blue-700 rounded-full text-xs font-medium hover:bg-blue-200">
                      {pickerPlatform} <X className="h-3 w-3" />
                    </button>
                  )}
                  {pickerCustomer !== 'all' && (
                    <button onClick={() => setPickerCustomer('all')} className="inline-flex items-center gap-1 px-2.5 py-0.5 bg-green-100 text-green-700 rounded-full text-xs font-medium hover:bg-green-200">
                      {selectedCustomer ? (selectedCustomer.name || selectedCustomer.customerName) : pickerCustomer} <X className="h-3 w-3" />
                    </button>
                  )}
                  {pickerSearch && (
                    <button onClick={() => setPickerSearch('')} className="inline-flex items-center gap-1 px-2.5 py-0.5 bg-gray-200 text-gray-600 rounded-full text-xs font-medium hover:bg-gray-300">
                      "{pickerSearch}" <X className="h-3 w-3" />
                    </button>
                  )}
                </div>
              )}

              {filtered.length === 0 ? (
                <div className="bg-white rounded-xl shadow-sm p-10 text-center">
                  <Search className="h-10 w-10 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500 font-medium">No matching assignments</p>
                  <p className="text-sm text-gray-400 mt-1">Try adjusting your filters or search term.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {filtered.map((a, i) => {
                    return (
                      <div
                        key={`${a.calendarId}-${a.itemIndex}-${i}`}
                        className="bg-white rounded-xl shadow-sm border border-transparent hover:border-purple-200 hover:shadow-md transition-all group overflow-hidden"
                      >
                        {/* Top accent bar by platform */}
                        <div className={`h-1 w-full ${(() => {
                          const fps = flatPlatforms(a.platform);
                          if (fps.includes('instagram')) return 'bg-gradient-to-r from-pink-400 to-purple-500';
                          if (fps.includes('facebook')) return 'bg-blue-500';
                          if (fps.includes('youtube')) return 'bg-red-500';
                          if (fps.includes('linkedin')) return 'bg-blue-700';
                          if (fps.includes('twitter')) return 'bg-sky-400';
                          return 'bg-purple-400';
                        })()}`} />
                        <div className="p-4 flex items-start gap-4">
                          {/* Index badge */}
                          <div className="w-8 h-8 rounded-lg bg-purple-100 text-purple-700 font-bold text-sm flex items-center justify-center flex-shrink-0 mt-0.5">
                            {i + 1}
                          </div>

                          <div className="flex-1 min-w-0">
                            {/* Title + badges row */}
                            <div className="flex items-start gap-2 flex-wrap">
                              <p className="font-bold text-gray-900 group-hover:text-purple-800 text-sm leading-snug flex-1 min-w-0">
                                {a.title || 'Untitled Assignment'}
                              </p>
                              <div className="flex items-center gap-1.5 flex-shrink-0 flex-wrap">
                                {parsePlatforms(a.platform || a.type).map((p, pi) => (
                                  <span key={pi} className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded border text-[10px] font-semibold ${platformColor(p)}`}>
                                    <PlatformIcon platform={p} className="h-2.5 w-2.5 flex-shrink-0" />
                                    {p.charAt(0).toUpperCase() + p.slice(1)}
                                  </span>
                                ))}
                                {(() => {
                                  const approval = getPickerItemApproval(a);
                                  return (
                                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${approval.color}`}>
                                      {approval.label}
                                    </span>
                                  );
                                })()}
                              </div>
                            </div>

                            {/* Calendar Name / Post Type row */}
                            {(a.calendarName || a.postType) && (
                              <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                                {a.calendarName && (
                                  <span className="flex items-center gap-1 text-xs text-gray-400">
                                    <Calendar className="h-3 w-3 flex-shrink-0" />
                                    {a.calendarName}
                                  </span>
                                )}
                                {a.postType && (
                                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-amber-50 text-amber-700 border border-amber-100 capitalize">
                                    <FileText className="h-2.5 w-2.5" />
                                    Type: {a.postType}
                                  </span>
                                )}
                              </div>
                            )}

                            {/* Description */}
                            {a.description && (
                              <p className="text-xs text-gray-400 mt-1 line-clamp-1">{a.description}</p>
                            )}

                            {/* Admin review status label */}
                            {(() => {
                              if (getPickerFilterStatus(a) === 'published') return null;
                              const reviewStatus = getPickerReviewStatus(a);
                              if (!reviewStatus) return null;
                              return (
                                <div className="mt-2">
                                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${reviewStatus.color}`}>
                                    {reviewStatus.label === 'Approved by Admin' && <span className="w-1.5 h-1.5 rounded-full bg-orange-500 inline-block" />}
                                    {reviewStatus.label}
                                  </span>
                                </div>
                              );
                            })()}

                            {/* Due date + action row */}
                            <div className="flex items-center justify-between mt-2.5 gap-2 flex-wrap">
                              <span className="flex items-center gap-1 text-xs font-medium text-gray-400">
                                <Clock className="h-3 w-3 flex-shrink-0" />
                                {a.dueDate
                                  ? 'Due ' + new Date(a.dueDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                                  : 'No due date'}
                              </span>
                              <button
                                onClick={() => navigate(`/content-creator/upload/${a.calendarId}/${a.itemIndex}`)}
                                className="flex items-center gap-1.5 px-4 py-1.5 bg-purple-600 hover:bg-purple-700 text-white text-xs font-semibold rounded-lg transition-colors shadow-sm hover:shadow-md flex-shrink-0"
                              >
                                <Send className="h-3 w-3" />
                                Upload
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </main>
          </div>
        )}
      </ContentCreatorLayout>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-50 to-indigo-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <span className="text-lg text-gray-700">Loading assignment...</span>
        </div>
      </div>
    );
  }

  if (!assignment) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-50 to-indigo-100">
        <div className="text-center p-8 bg-white rounded-lg shadow-lg">
          <span className="text-lg text-red-600">Assignment not found.</span>
          <button
            onClick={() => navigate('/content-creator/assignments')}
            className="mt-4 px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors"
          >
            Back to Assignments
          </button>
        </div>
      </div>
    );
  }

  const handleBackAction = () => {
    if (isAdmin) {
      navigate('/admin/dashboard');
      return;
    }
    if (assignment && assignment.customerId) {
      navigate(`/content-creator/assignments?expand=${assignment.customerId}`);
    } else {
      navigate('/content-creator/assignments');
    }
  };

  return (
    <ContentCreatorLayout
      title={isAdmin ? 'Admin Content Upload' : (activeTab === 'admin' ? 'Submit for Admin Review' : 'Content Details')}
      subtitle={isAdmin ? 'Upload and manage customer-facing content from the admin portal' : (activeTab === 'admin' ? 'Admin must approve before content goes to customer' : 'Review and manage customer-facing content submissions')}
      icon={<Palette className="h-5 w-5 text-white" />}
      fullWidthContent={true}
      onBack={handleBackAction}
      homePath={isAdmin ? '/admin/dashboard' : '/content-creator'}
    >
      <div>
        {/* ── Tabs Selector ── */}
        {!isAdmin && reviewTabMode === 'both' && (
          <div className="flex border-b border-gray-200 bg-white sm:rounded-2xl p-1.5 sm:shadow-sm sm:border sm:border-gray-200/50 mb-4 sm:mb-6">
            <button
              onClick={() => {
                setActiveTab('admin');
                const tabVersions = allPreviousVersions.filter(v => v.submissionStage !== 'customer' || v.sentToCustomerAt);
                if (tabVersions.length > 0) setSelectedVersionIndex(tabVersions.length - 1);
                else setSelectedVersionIndex(0);
                setSelectedMediaIndex(0);
              }}
              className={`flex-1 py-3 text-sm font-semibold rounded-xl transition-all duration-300 flex items-center justify-center gap-2 ${activeTab === 'admin'
                ? 'bg-gradient-to-r from-purple-600 to-indigo-600 text-white shadow-md'
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                }`}
            >
              <Palette className="h-4 w-4" />
              Admin Review
            </button>
            <button
              onClick={() => {
                setActiveTab('customer');
                const tabVersions = allPreviousVersions.filter(v => v.submissionStage === 'customer' || v.submissionStage === '');
                if (tabVersions.length > 0) setSelectedVersionIndex(tabVersions.length - 1);
                else setSelectedVersionIndex(0);
                setSelectedMediaIndex(0);
              }}
              className={`flex-1 py-3 text-sm font-semibold rounded-xl transition-all duration-300 flex items-center justify-center gap-2 ${activeTab === 'customer'
                ? 'bg-gradient-to-r from-purple-600 to-indigo-600 text-white shadow-md'
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                }`}
            >
              <User className="h-4 w-4" />
              Customer Review
            </button>
          </div>
        )}

        {/* Admin Uploaded Banner — shown when admin already sent content to customer */}
        {adminUploadedForThis && (
          <div className="bg-blue-50 border border-blue-200 sm:rounded-xl shadow-sm px-4 py-3 sm:px-5 sm:py-4 mb-4 sm:mb-6 flex flex-col sm:flex-row sm:items-start gap-3">
            <div className="p-2 bg-blue-100 rounded-lg flex-shrink-0">
              <ShieldCheck className="h-5 w-5 text-blue-600" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold text-blue-800">Admin Has Already Submitted Content</p>
              <p className="text-xs text-blue-700 mt-0.5">
                The admin has uploaded content for this assignment and sent it directly to the customer. No further upload is needed from you.
              </p>
            </div>
            <span className="flex-shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white text-xs font-semibold rounded-full">
              <Check className="h-3 w-3" />
              Admin Directly Sent to Customer
            </span>
          </div>
        )}

        {/* Admin Approval Banner — shown when the latest version is approved */}
        {activeTab === 'admin' && (() => {
          const latestApproved = [...previousVersions]
            .reverse()
            .find(v => v.status === 'approved' || v.status === 'approved_by_admin');
          if (!latestApproved) return null;
          return (
            <div className="bg-green-50 border border-green-200 sm:rounded-xl shadow-sm px-4 py-3 sm:px-5 sm:py-4 mb-4 sm:mb-6 flex flex-col sm:flex-row sm:items-start gap-3">
              <div className="p-2 bg-green-100 rounded-lg flex-shrink-0">
                <ShieldCheck className="h-5 w-5 text-green-600" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-bold text-green-800">Version {latestApproved.flowVersionNumber || latestApproved.versionNumber} Approved by Admin</p>
                {latestApproved.approvalNotes ? (
                  <p className="text-xs text-green-700 mt-0.5">{latestApproved.approvalNotes}</p>
                ) : (
                  <p className="text-xs text-green-600 mt-0.5">Your content has been reviewed and approved. It is ready to be sent to the customer.</p>
                )}
              </div>
              <span className="flex-shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 bg-green-600 text-white text-xs font-semibold rounded-full">
                <Check className="h-3 w-3" />
                Approved
              </span>
            </div>
          );
        })()}

        {/* Main Grid Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-6">
          {/* Left Column - Upload Section */}
          <div className="lg:col-span-2 space-y-4 sm:space-y-6">
            {/* Version History Accordion —  ContentDetails style */}
            <div className="space-y-2 sm:space-y-4">
              {/* Accordion trigger */}
              <div className="bg-white sm:rounded-2xl sm:shadow-sm sm:border sm:border-gray-200 overflow-hidden w-full p-3.5 sm:p-5 hover:bg-gray-50 transition-colors duration-200">
                <div className="flex flex-col gap-3">
                  {/* Top Row: Title + Icon (left) and Chevron (right) */}
                  <div className="flex items-center justify-between w-full">
                    <button
                      type="button"
                      onClick={() => setVersionsAccordionOpen(!versionsAccordionOpen)}
                      className="flex items-center gap-3 text-left focus:outline-none min-w-0 flex-1"
                    >
                      <div className="p-2 bg-green-100 rounded-lg flex-shrink-0">
                        <History className="h-5 w-5 text-green-600" />
                      </div>
                      <div className="min-w-0">
                        <h2 className="text-base sm:text-lg font-bold text-gray-900 truncate">Version History</h2>
                        <p className="text-xs sm:text-sm text-gray-500">
                          {previousVersions.length} {previousVersions.length === 1 ? 'version' : 'versions'} submitted
                        </p>
                      </div>
                    </button>

                    <button
                      type="button"
                      onClick={() => setVersionsAccordionOpen(!versionsAccordionOpen)}
                      className="p-1.5 rounded hover:bg-gray-100 focus:outline-none flex-shrink-0 ml-2"
                      aria-label="Toggle version history"
                    >
                      {versionsAccordionOpen
                        ? <ChevronUp className="h-5 w-5 text-gray-400" />
                        : <ChevronDown className="h-5 w-5 text-gray-400" />}
                    </button>
                  </div>

                  {/* Bottom Row: Optional action buttons (only visible when previousVersions.length === 0) */}
                  {previousSubmissionLoaded && previousVersions.length === 0 && (
                    <div className="flex items-center gap-3 flex-wrap sm:flex-nowrap justify-start sm:justify-end flex-grow mr-2 mt-1">
                      {(() => {
                        const isThumbnailAllowed = ['reel', 'video'].includes((assignment?.postType || '').toLowerCase());
                        return (
                          <>
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); onButtonClick(); }}
                              disabled={!canAddMoreMedia}
                              title={canAddMoreMedia ? 'Add media' : 'Only 1 media is allowed for this post type'}
                              className="inline-flex items-center px-4 py-2 text-sm font-medium bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              <Image className="h-4 w-4 mr-2" />
                              Create first version
                            </button>
                            {isThumbnailAllowed && (
                              <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); onThumbnailButtonClick(); }}
                                className="inline-flex items-center px-4 py-2 text-sm font-medium bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                              >
                                <Image className="h-4 w-4 mr-2" />
                                Add thumbnail
                              </button>
                            )}
                          </>
                        );
                      })()}
                    </div>
                  )}
                </div>

                {/* Hidden inputs always rendered in DOM */}
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept="image/*,video/*"
                  onChange={handleChange}
                  className="hidden"
                />
                <input
                  ref={thumbnailInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleThumbnailChange}
                  className="hidden"
                />
                <input
                  ref={replaceFileInputRef}
                  type="file"
                  accept="image/*,video/*"
                  onChange={handleReplaceChange}
                  className="hidden"
                />

                {previousSubmissionLoaded && previousVersions.length === 0 && (() => {
                  const isThumbnailAllowed = ['reel', 'video'].includes((assignment?.postType || '').toLowerCase());
                  return (
                    <>

                      {isThumbnailAllowed && thumbnailFileObj && (
                        <div className="mt-3 p-3 bg-indigo-50/80 border border-indigo-100 rounded-xl flex items-center justify-between shadow-sm">
                          <div className="flex items-center gap-3">
                            <img src={thumbnailFileObj.preview} alt="Thumbnail preview" className="w-12 h-12 object-cover rounded-lg border border-indigo-200" />
                            <div className="min-w-0">
                              <p className="text-xs font-semibold text-indigo-900 truncate">{thumbnailFileObj.name}</p>
                              <p className="text-[10px] text-indigo-600">{formatFileSize(thumbnailFileObj.size)}</p>
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => setThumbnailFileObj(null)}
                            className="p-1 rounded-full text-indigo-400 hover:text-red-500 hover:bg-red-50 transition-colors"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      )}

                      {/* Ã¢â€â‚¬Ã¢â€â‚¬ WhatsApp-style compact thumbnail strip for initial upload (No versions yet) Ã¢â€â‚¬Ã¢â€â‚¬ */}
                      {uploadedFiles.length > 0 && (
                        <div className="mt-3 flex flex-wrap items-center gap-2">
                          {uploadedFiles.map((file) => (
                            <div
                              key={file.id}
                              className="relative group w-16 h-16 rounded-lg overflow-hidden bg-gray-100 ring-1 ring-gray-200 hover:ring-purple-400 transition-all cursor-pointer flex-shrink-0"
                              onClick={() => handleReplaceClick(file.id)}
                              title={`${file.name} — click to replace`}
                            >
                              {/* Thumbnail preview */}
                              {file.type === 'image' ? (
                                <img
                                  src={file.preview}
                                  alt={file.name}
                                  className="w-full h-full object-cover"
                                />
                              ) : (
                                <div className="relative w-full h-full">
                                  <video
                                    src={file.preview}
                                    className="w-full h-full object-cover"
                                    muted
                                  />
                                  <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                                    <Play className="h-4 w-4 text-white" />
                                  </div>
                                </div>
                              )}

                              {/* Top-left badge: green check (existing) or purple NEW */}
                              <div className={`absolute top-0.5 left-0.5 flex items-center justify-center rounded text-[8px] font-bold leading-none z-10 ${file.isExisting
                                ? 'w-3.5 h-3.5 bg-emerald-500 text-white'
                                : 'px-1 py-0.5 bg-purple-600 text-white'
                                }`}>
                                {file.isExisting ? <Check className="h-2.5 w-2.5" /> : 'NEW'}
                              </div>

                              {/* Spinner overlay when uploading */}
                              {file.uploading && (
                                <div className="absolute inset-0 bg-black/50 flex items-center justify-center z-20">
                                  <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" />
                                </div>
                              )}

                              {/* Error overlay */}
                              {file.error && (
                                <div className="absolute inset-0 bg-red-500/60 flex items-center justify-center z-20">
                                  <X className="h-4 w-4 text-white" />
                                </div>
                              )}

                              {/* Hover remove button (top-right) */}
                              <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); removeFile(file.id); }}
                                className="absolute top-0 right-0 w-4 h-4 bg-red-500 hover:bg-red-600 text-white rounded-bl-md flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity z-30"
                                title="Remove"
                              >
                                <X className="h-2.5 w-2.5" />
                              </button>
                            </div>
                          ))}

                          {/* "+" add-media tile */}
                          {canAddMoreMedia && (
                            <button
                              type="button"
                              onClick={onButtonClick}
                              className="w-16 h-16 rounded-lg border-2 border-dashed border-gray-300 hover:border-purple-400 hover:bg-purple-50 flex items-center justify-center transition-all flex-shrink-0"
                              title="Add media"
                            >
                              <Plus className="h-6 w-6 text-gray-400 group-hover:text-purple-500" />
                            </button>
                          )}
                        </div>
                      )}
                    </>
                  );
                })()}


                {/* Validation notice */}
                {validationNotice && (
                  <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-2.5 text-sm text-amber-800">
                    {validationNotice}
                  </div>
                )}
              </div>

              {/* Accordion body */}
              {versionsAccordionOpen && (
                <div className="">
                  {!previousSubmissionLoaded ? (
                    <div className="text-center py-12">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto mb-3"></div>
                      <p className="text-gray-600 font-medium">Loading versions...</p>
                    </div>
                  ) : previousVersions.length === 0 ? (
                    <div className="text-center py-12">
                      <div className="bg-gray-100 rounded-full w-14 h-14 flex items-center justify-center mx-auto mb-3">
                        <History className="h-7 w-7 text-gray-400" />
                      </div>
                      <p className="text-gray-600 font-medium">No versions submitted yet</p>
                      <p className="text-sm text-gray-400 mt-1">Upload your first version using the form above</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 xl:gap-6">
                      {/* ── Left 2/3: Version display ── */}
                      <div className="xl:col-span-2">
                        <div className="bg-gray-50 sm:rounded-2xl sm:border sm:border-gray-200 overflow-hidden">
                          {/* Version display header */}
                          <div className="px-3.5 py-3 border-b border-gray-100 bg-white">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <div className="p-2 bg-purple-100 rounded-lg">
                                  <Image className="h-4 w-4 text-purple-600" />
                                </div>
                                <div>
                                  <h3 className="text-base font-semibold text-gray-900">
                                    Version {previousVersions[selectedVersionIndex]?.flowVersionNumber || previousVersions[selectedVersionIndex]?.versionNumber}
                                  </h3>
                                  <p className="text-xs text-gray-500">of {allPreviousVersions.length} total versions</p>
                                </div>
                              </div>
                              <div className="flex flex-col gap-2 items-end max-w-xs">
                                {/* Version pills — sequential, no admin/customer label */}
                                <div className="flex items-center gap-1.5 flex-wrap justify-end">
                                  {allPreviousVersions.map((v, i) => {
                                    const seqNum = i + 1;
                                    const stage = v.submissionStage === 'customer' ? 'customer' : 'admin';
                                    const isSelected = selectedVersionId === v.id && activeTab === stage;
                                    return (
                                      <button
                                        key={v.id || i}
                                        onClick={() => {
                                          setActiveTab(stage);
                                          const stageList = stage === 'admin' ? adminVersions : customerVersions;
                                          const stageIdx = stageList.findIndex(sv => sv.id === v.id);
                                          setSelectedVersionIndex(stageIdx >= 0 ? stageIdx : 0);
                                          setSelectedMediaIndex(0);
                                        }}
                                        className={`px-2.5 py-1 rounded-full text-xs font-semibold transition-all ${isSelected
                                          ? 'bg-blue-700 text-white shadow-sm'
                                          : 'bg-gray-100 text-gray-600 hover:bg-blue-100 hover:text-blue-700'
                                          }`}
                                      >
                                        V{seqNum}
                                      </button>
                                    );
                                  })}
                                </div>
                              </div>
                            </div>
                          </div>

                          <div className="p-3 sm:p-5">
                            {previousVersions[selectedVersionIndex] && (
                              <div className="space-y-4 sm:space-y-5">
                                {/* Media */}
                                <div>
                                  {(() => {
                                    const version = previousVersions[selectedVersionIndex];
                                    const mediaItems = version?.media || [];
                                    const thumbUrl = version?.thumbnailUrl || null;

                                    const isLatestVersion = allPreviousVersions.length > 0 && version?.id === allPreviousVersions[allPreviousVersions.length - 1]?.id;
                                    const revisionStatuses = ['revision_requested', 'changes_requested', 'changes_requested_admin', 'changes_requested_customer_approved_admin'];
                                    const isRevisionRequested = allPreviousVersions.length > 0 && revisionStatuses.includes(allPreviousVersions[allPreviousVersions.length - 1]?.status);
                                    const isEditable = isLatestVersion && isRevisionRequested;
                                    const isThumbnailAllowed = ['reel', 'video'].includes((assignment?.postType || '').toLowerCase());

                                    // Read-only slides: version media + thumbnail slide if present
                                    const readOnlySlides = thumbUrl
                                      ? [...mediaItems, { url: thumbUrl, type: 'image', isThumbnailSlide: true }]
                                      : mediaItems;

                                    // Editable slides: uploadedFiles (draft/revision media) + current active thumbnail slide
                                    const currentThumbUrl = thumbnailFileRemoved
                                      ? null
                                      : (thumbnailFileObj
                                        ? (thumbnailFileObj.preview || thumbnailFileObj.publicUrl)
                                        : (version?.thumbnailUrl || null));

                                    const editableSlides = currentThumbUrl
                                      ? [
                                        ...uploadedFiles.map(file => ({
                                          url: file.preview || file.publicUrl,
                                          type: file.type,
                                          id: file.id,
                                          fileObj: file,
                                          isDraft: true
                                        })),
                                        {
                                          url: currentThumbUrl,
                                          type: 'image',
                                          isThumbnailSlide: true,
                                          isDraft: true,
                                          fileObj: thumbnailFileObj
                                        }
                                      ]
                                      : uploadedFiles.map(file => ({
                                        url: file.preview || file.publicUrl,
                                        type: file.type,
                                        id: file.id,
                                        fileObj: file,
                                        isDraft: true
                                      }));

                                    const slides = isEditable ? editableSlides : readOnlySlides;
                                    const totalSlides = slides.length;
                                    const safeMediaIndex = Math.min(selectedMediaIndex, Math.max(0, totalSlides - 1));
                                    const currentSlide = slides[safeMediaIndex];
                                    const isThumbnailSlide = currentSlide?.isThumbnailSlide === true;

                                    if (totalSlides === 0) {
                                      return (
                                        <div className="h-64 bg-gray-200 rounded-xl flex items-center justify-center">
                                          <div className="text-center">
                                            <Image className="h-12 w-12 text-gray-400 mx-auto mb-2" />
                                            <p className="text-gray-500">No media available</p>
                                          </div>
                                        </div>
                                      );
                                    }

                                    return (
                                      <div>
                                        {totalSlides > 1 && (
                                          <div className="flex items-center justify-between mb-3 px-1">
                                            <span className="text-xs text-gray-600">
                                              {safeMediaIndex + 1} / {totalSlides}
                                              {isThumbnailSlide && (
                                                <span className="ml-1.5 text-indigo-500 font-semibold">(Thumbnail)</span>
                                              )}
                                            </span>
                                            <div className="flex gap-2">
                                              <button
                                                onClick={() => {
                                                  if (selectedMediaIndex > 0) setSelectedMediaIndex(selectedMediaIndex - 1);
                                                }}
                                                disabled={safeMediaIndex === 0}
                                                className="p-1.5 rounded-lg bg-blue-50 border border-gray-200 hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                              >
                                                <ChevronLeft className="h-3.5 w-3.5 text-blue-600" />
                                              </button>
                                              <button
                                                onClick={() => {
                                                  if (selectedMediaIndex < totalSlides - 1) setSelectedMediaIndex(selectedMediaIndex + 1);
                                                }}
                                                disabled={safeMediaIndex === totalSlides - 1}
                                                className="p-1.5 rounded-lg bg-blue-50 border border-gray-200 hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                              >
                                                <ChevronRight className="h-3.5 w-3.5 text-blue-600" />
                                              </button>
                                            </div>
                                          </div>
                                        )}

                                        {otherSlidesWithComments.length > 0 && (
                                          <div className="mb-3 px-3 py-1.5 bg-amber-50 border border-amber-200 rounded-lg text-[11px] text-amber-700 font-medium flex items-center flex-wrap gap-1.5">
                                            <AlertCircle className="h-3.5 w-3.5 text-amber-600 flex-shrink-0 mt-0.5 animate-bounce" />
                                            <span>Comments found on:</span>
                                            <div className="flex items-center gap-1">
                                              {otherSlidesWithComments.map(slideNum => {
                                                const isThumb = slides[slideNum - 1]?.isThumbnailSlide === true;
                                                const label = isThumb ? 'Thumbnail' : `Slide ${slideNum}`;
                                                return (
                                                  <button
                                                    key={slideNum}
                                                    type="button"
                                                    onClick={() => setSelectedMediaIndex(slideNum - 1)}
                                                    className="px-1.5 py-0.5 bg-amber-100 hover:bg-amber-200 border border-amber-300 rounded text-[10px] font-bold text-amber-800 transition-colors cursor-pointer focus:outline-none focus:ring-1 focus:ring-amber-500"
                                                    title={`Go to ${isThumb ? 'thumbnail' : `slide ${slideNum}`}`}
                                                  >
                                                    {label}
                                                  </button>
                                                );
                                              })}
                                            </div>
                                          </div>
                                        )}

                                        <div className="flex items-center justify-between mb-2">
                                          <p className="text-[10px] text-gray-400">
                                            {isThumbnailSlide
                                              ? 'Video thumbnail'
                                              : isEditable
                                                ? 'Draft media preview'
                                                : `${commentsForCurrentMedia.length} comment${commentsForCurrentMedia.length !== 1 ? 's' : ''} on this media`}
                                          </p>
                                        </div>

                                        {/* Current slide */}
                                        <div className="flex justify-center">
                                          <div className="relative inline-block">
                                            {isThumbnailSlide ? (
                                              /* Thumbnail slide */
                                              <div className="relative">
                                                <img
                                                  src={currentThumbUrl}
                                                  alt="Video Thumbnail"
                                                  className="max-w-full h-auto max-h-[50vh] sm:max-h-[60vh] lg:max-h-[70vh] rounded-lg shadow border border-indigo-200 object-contain"
                                                />
                                                <span className="absolute bottom-2 left-1/2 -translate-x-1/2 bg-indigo-600/80 text-white text-[10px] font-semibold px-2.5 py-1 rounded-full backdrop-blur-sm whitespace-nowrap">
                                                  Thumbnail
                                                </span>
                                              </div>
                                            ) : currentSlide?.url && typeof currentSlide.url === 'string' ? (
                                              currentSlide.type === 'image' ? (
                                                <img
                                                  ref={versionImgRef}
                                                  src={currentSlide.url}
                                                  alt={`Version ${version.versionNumber} - Media ${safeMediaIndex + 1}`}
                                                  className="max-w-full h-auto max-h-[50vh] sm:max-h-[60vh] lg:max-h-[70vh] rounded-lg shadow border border-gray-200 object-contain"
                                                  onLoad={handleVersionImgLoad}
                                                  onError={(e) => { e.target.style.display = 'none'; }}
                                                />
                                              ) : (
                                                <video
                                                  ref={videoRef}
                                                  src={currentSlide.url}
                                                  controls
                                                  className="max-w-full h-auto max-h-[50vh] sm:max-h-[60vh] lg:max-h-[70vh] rounded-lg shadow border border-gray-200 object-contain"
                                                />
                                              )
                                            ) : (
                                              <div className="w-80 h-64 bg-gray-200 rounded-xl flex items-center justify-center">
                                                <div className="text-center">
                                                  <Image className="h-12 w-12 text-gray-400 mx-auto mb-2" />
                                                  <p className="text-gray-500">Media unavailable</p>
                                                </div>
                                              </div>
                                            )}

                                            {/* Comment markers */}
                                            {commentsForCurrentMedia.map((comment, index) => {
                                              const commentX = comment.x || comment.position?.x || 0;
                                              const commentY = comment.y || comment.position?.y || 0;
                                              const pctX = commentX <= 1 ? commentX * 100 : commentX;
                                              const pctY = commentY <= 1 ? commentY * 100 : commentY;
                                              let boxLeft = 40;
                                              let boxRight = 'auto';
                                              if (pctX > 50) { boxLeft = 'auto'; boxRight = 40; }
                                              return (
                                                <div
                                                  key={comment.id}
                                                  style={{
                                                    position: 'absolute',
                                                    top: `${pctY}%`, left: `${pctX}%`,
                                                    transform: 'translate(-50%, -50%)',
                                                    width: 24, height: 24,
                                                    background: comment.done ? '#10b981' : '#ef4444',
                                                    color: '#fff', borderRadius: '50%',
                                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                    fontWeight: 'bold', fontSize: '11px',
                                                    boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                                                    cursor: 'pointer', zIndex: 10, border: '2px solid #fff',
                                                  }}
                                                  onMouseEnter={() => setHoveredVersionComment(comment.id)}
                                                  onMouseLeave={() => setHoveredVersionComment(null)}
                                                  onClick={(e) => { e.stopPropagation(); handleVersionCommentClick(comment.id); }}
                                                >
                                                  {index + 1}
                                                  {(activeVersionComment === comment.id || hoveredVersionComment === comment.id) && (
                                                    <div
                                                      style={{
                                                        position: 'absolute',
                                                        left: boxLeft, right: boxRight,
                                                        top: '50%', transform: 'translateY(-50%)',
                                                        background: '#fff', border: '1px solid #3b82f6',
                                                        borderRadius: '8px', padding: '12px',
                                                        minWidth: '200px', maxWidth: '280px',
                                                        zIndex: 20, boxShadow: '0 4px 20px rgba(59,130,246,0.15)',
                                                      }}
                                                      onClick={(e) => e.stopPropagation()}
                                                    >
                                                      <div className="mb-2">
                                                        <p className="font-medium text-gray-900 text-sm leading-relaxed break-words">
                                                          {comment.message || comment.comment}
                                                          {comment.done && <span className="text-green-600 ml-2 text-xs">✓ Done</span>}
                                                        </p>
                                                        <p className="text-xs text-gray-500 mt-1">
                                                          {comment.timestamp ? new Date(comment.timestamp).toLocaleString() : ''}
                                                        </p>
                                                      </div>
                                                      {(() => {
                                                        const replies = comment.replies || (comment.reply ? [{
                                                          id: 'legacy-creator-reply',
                                                          authorRole: 'creator',
                                                          authorName: comment.reply.creatorName || 'Creator',
                                                          message: comment.reply.text,
                                                          timestamp: comment.reply.timestamp
                                                        }] : []);
                                                        return replies.map((rep, rIdx) => (
                                                          <div key={rep.id || rIdx} className="mt-2 p-2 bg-indigo-50 border border-indigo-200 rounded-lg">
                                                            <p className="text-[10px] font-bold text-indigo-700 mb-0.5">
                                                              Reply by {rep.authorRole === 'admin' ? 'Admin' : rep.authorRole === 'creator' ? 'Creator' : 'Customer'}
                                                            </p>
                                                            <p className="text-xs text-gray-800 break-words">{rep.message || rep.text}</p>
                                                          </div>
                                                        ));
                                                      })()}
                                                      <button
                                                        onClick={() => handleVersionToggleDone(comment.id)}
                                                        className={`w-full px-3 py-1.5 text-xs rounded-lg font-medium transition-all flex items-center justify-center mt-2 ${comment.done
                                                          ? 'bg-gray-200 hover:bg-gray-300 text-gray-700'
                                                          : 'bg-green-600 hover:bg-green-700 text-white'
                                                          }`}
                                                      >
                                                        <CheckCircle className="h-3 w-3 mr-1" />
                                                        {comment.done ? 'Undo Done' : 'Mark as Done'}
                                                      </button>
                                                    </div>
                                                  )}
                                                </div>
                                              );
                                            })}
                                          </div>
                                        </div>

                                        {/* -- Media Preview Strip -- */}
                                        {totalSlides > 0 && (
                                          <div className="flex flex-wrap justify-center items-center gap-2 mt-4 bg-gray-50/50 p-2 sm:p-3 rounded-xl border border-gray-100">
                                            {slides.map((slide, idx) => {
                                              const isSelected = idx === safeMediaIndex;
                                              const isDraftFile = slide.isDraft;
                                              const file = slide.fileObj; // fileObj exists if uploadedFiles file OR newly chosen thumbnailObj

                                              return (
                                                <div
                                                  key={isDraftFile && file ? file.id : (slide.id || idx)}
                                                  className={`relative group w-16 h-16 rounded-lg overflow-hidden bg-white hover:ring-purple-400 transition-all cursor-pointer flex-shrink-0 ${isSelected ? 'ring-2 ring-purple-500 shadow-md' : 'ring-1 ring-gray-200'
                                                    }`}
                                                  onClick={() => setSelectedMediaIndex(idx)}
                                                  title={slide.isThumbnailSlide ? 'Thumbnail — click to view' : isDraftFile ? `${file?.name} — click to view` : `Slide ${idx + 1} — click to view`}
                                                >
                                                  {/* Thumbnail preview */}
                                                  {slide.isThumbnailSlide ? (
                                                    <div className="relative w-full h-full">
                                                      <img
                                                        src={slide.url}
                                                        alt="Thumbnail"
                                                        className="w-full h-full object-cover"
                                                      />
                                                      <div className="absolute inset-x-0 bottom-0 bg-indigo-600/80 text-white text-[8px] font-bold text-center py-0.5 whitespace-nowrap">
                                                        THUMB
                                                      </div>
                                                    </div>
                                                  ) : slide.type === 'image' ? (
                                                    <img
                                                      src={slide.url}
                                                      alt="Preview"
                                                      className="w-full h-full object-cover"
                                                    />
                                                  ) : (
                                                    <div className="relative w-full h-full">
                                                      <video
                                                        src={slide.url}
                                                        className="w-full h-full object-cover"
                                                        muted
                                                      />
                                                      <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                                                        <Play className="h-4 w-4 text-white" />
                                                      </div>
                                                    </div>
                                                  )}

                                                  {/* EDITABLE BUTTONS/OVERLAYS (only when isEditable is true) */}
                                                  {isEditable && (
                                                    <>
                                                      {/* Top-left status badge */}
                                                      {!slide.isThumbnailSlide && file && (
                                                        <div className={`absolute top-0.5 left-0.5 flex items-center justify-center rounded text-[8px] font-bold leading-none z-10 ${file.isExisting
                                                          ? 'w-3.5 h-3.5 bg-emerald-500 text-white'
                                                          : file.isReplaced
                                                            ? 'px-1 py-0.5 bg-blue-500 text-white'
                                                            : 'px-1 py-0.5 bg-purple-600 text-white'
                                                          }`}>
                                                          {file.isExisting ? <Check className="h-2.5 w-2.5" /> : file.isReplaced ? 'REPLACE' : 'NEW'}
                                                        </div>
                                                      )}

                                                      {/* Uploading indicator */}
                                                      {file?.uploading && (
                                                        <div className="absolute inset-0 bg-black/50 flex items-center justify-center z-20">
                                                          <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" />
                                                        </div>
                                                      )}

                                                      {/* Error indicator */}
                                                      {file?.error && (
                                                        <div className="absolute inset-0 bg-red-500/60 flex items-center justify-center z-20">
                                                          <X className="h-4 w-4 text-white" />
                                                        </div>
                                                      )}

                                                      {/* Replace button (bottom-right) */}
                                                      <button
                                                        type="button"
                                                        onClick={(e) => {
                                                          e.stopPropagation();
                                                          if (slide.isThumbnailSlide) {
                                                            onThumbnailButtonClick();
                                                          } else {
                                                            handleReplaceClick(file.id);
                                                          }
                                                        }}
                                                        className="absolute bottom-0 right-0 w-6 h-6 bg-blue-500 hover:bg-blue-600 text-white rounded-tl-md flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity z-30 shadow-sm"
                                                        title={slide.isThumbnailSlide ? 'Replace Thumbnail' : 'Replace Media'}
                                                      >
                                                        <Edit3 className="h-3 w-3" />
                                                      </button>

                                                      {/* Remove button (top-right) */}
                                                      <button
                                                        type="button"
                                                        onClick={(e) => {
                                                          e.stopPropagation();
                                                          if (slide.isThumbnailSlide) {
                                                            if (thumbnailFileObj) {
                                                              setThumbnailFileObj(null);
                                                            } else {
                                                              setThumbnailFileRemoved(true);
                                                            }
                                                          } else {
                                                            removeFile(file.id);
                                                          }
                                                        }}
                                                        className="absolute top-0 right-0 w-4 h-4 bg-red-500 hover:bg-red-600 text-white rounded-bl-md flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity z-30"
                                                        title="Remove"
                                                      >
                                                        <X className="h-2.5 w-2.5" />
                                                      </button>
                                                    </>
                                                  )}
                                                </div>
                                              );
                                            })}

                                            {/* "+" add-media tile (only in edit/upload mode when isEditable is true) */}
                                            {isEditable && canAddMoreMedia && (
                                              <button
                                                type="button"
                                                onClick={onButtonClick}
                                                className="w-16 h-16 rounded-lg border-2 border-dashed border-gray-300 hover:border-purple-400 hover:bg-purple-50 flex items-center justify-center transition-all flex-shrink-0 bg-white"
                                                title="Add media"
                                              >
                                                <Plus className="h-6 w-6 text-gray-400" />
                                              </button>
                                            )}

                                            {/* "+" add-thumbnail tile (only in edit/upload mode when isEditable is true, thumbnail is allowed and not present) */}
                                            {isEditable && isThumbnailAllowed && !currentThumbUrl && (
                                              <button
                                                type="button"
                                                onClick={onThumbnailButtonClick}
                                                className="w-16 h-16 rounded-lg border-2 border-dashed border-indigo-300 hover:border-indigo-500 hover:bg-indigo-50 flex flex-col items-center justify-center transition-all flex-shrink-0 bg-white"
                                                title="Add thumbnail"
                                              >
                                                <Plus className="h-4 w-4 text-indigo-500 mb-0.5" />
                                                <span className="text-[8px] font-bold text-indigo-600">THUMB</span>
                                              </button>
                                            )}

                                            {/* Submit button (only if there are new files added or replaced, in edit mode) */}
                                            {isEditable && (uploadedFiles.some(f => !f.isExisting) || thumbnailFileObj !== null || thumbnailFileRemoved) && (
                                              <button
                                                type="button"
                                                onClick={handleSubmit}
                                                disabled={uploadedFiles.some(f => f.uploading) || submitting}
                                                className="w-12 h-12 rounded-full bg-green-500 hover:bg-green-600 disabled:bg-green-300 text-white flex items-center justify-center transition-all shadow-md group ml-2 flex-shrink-0"
                                                title="Send to Admin"
                                              >
                                                {submitting ? (
                                                  <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" />
                                                ) : (
                                                  <Send className="h-5 w-5 transform group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
                                                )}
                                              </button>
                                            )}
                                          </div>
                                        )}
                                      </div>
                                    );
                                  })()}
                                </div>

                                <div className="flex flex-col sm:flex-row sm:items-center justify-between text-sm text-gray-500 gap-2">
                                  <span>Created: {formatVersionDateLong(previousVersions[selectedVersionIndex].createdAt)}</span>
                                  <div className="flex items-center gap-2 flex-wrap justify-start sm:justify-end">
                                    {(previousVersions[selectedVersionIndex].status === 'approved' ||
                                      previousVersions[selectedVersionIndex].status === 'approved_by_admin') && (
                                        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-700 border border-green-200">
                                          <ShieldCheck className="h-3 w-3" />
                                          Approved by Admin
                                        </span>
                                      )}
                                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${getVersionStatusColor(previousVersions[selectedVersionIndex].status)}`}>
                                      {(previousVersions[selectedVersionIndex].status || '').replace(/_/g, ' ').toUpperCase()}
                                    </span>
                                  </div>
                                </div>
                                {(previousVersions[selectedVersionIndex].status === 'approved' ||
                                  previousVersions[selectedVersionIndex].status === 'approved_by_admin') &&
                                  previousVersions[selectedVersionIndex].approvalNotes && (
                                    <div className="mt-2 p-3 bg-green-50 border border-green-200 rounded-lg flex items-start gap-2">
                                      <ShieldCheck className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />
                                      <div>
                                        <p className="text-xs font-semibold text-green-700 mb-0.5">Admin Approval Note</p>
                                        <p className="text-xs text-green-800">{previousVersions[selectedVersionIndex].approvalNotes}</p>
                                      </div>
                                    </div>
                                  )}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Ã¢â€â‚¬Ã¢â€â‚¬ Right 1/3: Comments Ã¢â€â‚¬Ã¢â€â‚¬ */}
                      <div className="space-y-4 sm:space-y-5">
                        {/* Comments panel */}
                        <div className="bg-white sm:rounded-2xl sm:shadow-sm sm:border sm:border-gray-200 overflow-hidden">
                          <div className="px-3.5 py-3 border-b border-gray-100 bg-gray-50">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <div className="p-2 bg-purple-100 rounded-lg">
                                  <MessageSquare className="h-4 w-4 text-purple-600" />
                                </div>
                                <div>
                                  <h3 className="text-base font-semibold text-gray-900">Comments</h3>
                                  <p className="text-xs text-gray-500">
                                    Version {previousVersions[selectedVersionIndex]?.flowVersionNumber || previousVersions[selectedVersionIndex]?.versionNumber} • Media {selectedMediaIndex + 1}
                                  </p>
                                </div>
                              </div>
                              <span className="text-sm font-semibold text-purple-600 bg-purple-100 px-2.5 py-1 rounded-full">
                                {commentsForCurrentMedia.length}
                              </span>
                            </div>
                          </div>
                          <div className="max-h-96 overflow-y-auto px-1.5 py-3">
                            {commentsForCurrentMedia.length === 0 ? (
                              <div className="text-center py-10">
                                <div className="bg-gray-100 rounded-full w-14 h-14 flex items-center justify-center mx-auto mb-3">
                                  <MessageSquare className="h-6 w-6 text-gray-400" />
                                </div>
                                <p className="text-gray-500 text-sm font-medium">No comments yet</p>
                                <p className="text-gray-400 text-xs mt-1">Comments will appear here when added</p>
                              </div>
                            ) : (
                              <div className="space-y-2.5">
                                {commentsForCurrentMedia.map((comment, idx) => (
                                  <div
                                    key={comment.id || idx}
                                    className={`flex flex-col gap-2 p-3 rounded-xl border transition-all duration-200 ${activeVersionComment === comment.id
                                      ? 'bg-purple-50/20 border-purple-200 shadow-sm'
                                      : 'bg-slate-50/10 border-slate-100 hover:bg-slate-50/30 hover:border-slate-200'
                                      }`}
                                  >
                                    {/* The initial comment bubble (Left aligned) */}
                                    <div
                                      className={`flex items-start gap-2 max-w-[90%] cursor-pointer group ${isCreatorOwnedEntry(comment) ? 'self-end flex-row-reverse' : 'self-start'}`}
                                      onClick={() => handleVersionCommentClick(comment.id)}
                                    >
                                      <span className="font-bold text-white bg-purple-500 rounded-full w-5 h-5 flex items-center justify-center text-[10px] flex-shrink-0 mt-1 shadow-sm">
                                        {idx + 1}
                                      </span>
                                      <div className={`shadow-sm px-3 py-2 flex flex-col relative ${isCreatorOwnedEntry(comment)
                                          ? 'bg-[#E7FFDB] border border-[#d3f5c0] rounded-2xl rounded-tr-sm'
                                          : (activeVersionComment === comment.id ? 'bg-purple-100 border border-purple-200 rounded-2xl rounded-tl-sm' : 'bg-white border border-gray-200 rounded-2xl rounded-tl-sm')
                                        }`}>
                                        <div className="flex items-center gap-1 mb-0.5">
                                          <span className="text-[9px] font-bold text-blue-700">
                                            {comment.authorRole === 'admin' ? 'Admin' : (comment.authorRole === 'customer' || comment.reviewType === 'external' ? (assignment?.customerName || assignment?.customer_name || comment.authorName || (comment.authorEmail ? comment.authorEmail.split('@')[0] : 'Customer')) : 'Creator')}
                                          </span>
                                          {comment.authorEmail && !(comment.authorRole === 'customer' || comment.reviewType === 'external') && (
                                            <span className="text-[9px] text-gray-500 truncate max-w-[110px]">• {comment.authorEmail}</span>
                                          )}
                                        </div>
                                        <p className="text-[13px] text-gray-800 break-words leading-snug">
                                          {comment.message || comment.comment}
                                        </p>

                                        <div className="flex items-center justify-end gap-2 mt-1.5 pt-1.5 border-t border-gray-100">
                                          <span className="text-[9px] text-gray-400">
                                            {comment.timestamp ? new Date(comment.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''}
                                          </span>

                                          {comment.done ? (
                                            <button
                                              onClick={(e) => { e.stopPropagation(); handleVersionToggleDone(comment.id); }}
                                              className="flex items-center gap-0.5 text-[10px] text-emerald-600 hover:text-emerald-700 transition-colors font-medium ml-1"
                                            >
                                              <CheckCircle className="h-3 w-3" /> Done
                                            </button>
                                          ) : (
                                            <button
                                              onClick={(e) => { e.stopPropagation(); handleVersionToggleDone(comment.id); }}
                                              className="flex items-center gap-0.5 text-[10px] text-gray-400 hover:text-emerald-600 transition-colors font-medium ml-1"
                                            >
                                              <CheckCircle className="h-3 w-3" /> Mark
                                            </button>
                                          )}

                                          {canReplyToEntry(comment) && (
                                            <button
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                if (replyingToComment === comment.id) {
                                                  setReplyingToComment(null);
                                                  setReplyText('');
                                                  setEditingReplyId(null);
                                                } else {
                                                  setReplyingToComment(comment.id);
                                                  setReplyText('');
                                                  setEditingReplyId(null);
                                                }
                                              }}
                                              className="flex items-center gap-0.5 text-[10px] text-indigo-500 hover:text-indigo-700 transition-colors font-medium ml-1"
                                            >
                                              <MessageSquare className="h-3 w-3" /> Reply
                                            </button>
                                          )}
                                          {isCreatorOwnedEntry(comment) && (
                                            <button
                                              onClick={(e) => { e.stopPropagation(); handleVersionCommentDelete(comment.id); }}
                                              className="flex items-center gap-0.5 text-[10px] text-red-500 hover:text-red-700 transition-colors font-medium ml-1"
                                            >
                                              <Trash2 className="h-3 w-3" /> Delete
                                            </button>
                                          )}
                                        </div>
                                      </div>
                                    </div>

                                    {/* Existing replies */}
                                    {(() => {
                                      let replies = comment.replies || (comment.reply ? [{
                                        id: 'legacy-creator-reply',
                                        authorRole: 'creator',
                                        authorName: comment.reply.creatorName || 'Creator',
                                        message: comment.reply.text,
                                        timestamp: comment.reply.timestamp
                                      }] : []);



                                      return replies.map((rep, rIdx) => {
                                        const isCreator = rep.authorRole === 'creator';
                                        return (
                                          <div
                                            key={rep.id || rIdx}
                                            className={`flex items-start gap-2 max-w-[90%] group ${isCreator ? 'self-end flex-row-reverse' : 'self-start'}`}
                                          >
                                            <div className="w-5 h-5 flex-shrink-0 flex items-center justify-center rounded-full bg-gray-100 border border-gray-200 text-[9px] font-bold text-gray-500 mt-1 shadow-sm">
                                              {(() => {
                                                let name = '';
                                                if (rep.authorRole === 'admin') {
                                                  name = rep.authorName || (rep.authorEmail ? rep.authorEmail.split('@')[0] : '') || 'Admin';
                                                } else if (rep.authorRole === 'creator') {
                                                  name = rep.authorName || (rep.authorEmail ? rep.authorEmail.split('@')[0] : '') || 'Creator';
                                                } else {
                                                  name = rep.authorName || (rep.authorEmail ? rep.authorEmail.split('@')[0] : '') || assignment?.customerName || assignment?.customer_name || 'Customer';
                                                }
                                                return name.trim().charAt(0).toUpperCase() || 'U';
                                              })()}
                                            </div>

                                            <div className={`px-3 py-2 shadow-sm flex flex-col relative ${isCreator
                                              ? 'bg-[#E7FFDB] border border-[#d3f5c0] rounded-2xl rounded-tr-sm'
                                              : 'bg-white border border-gray-200 rounded-2xl rounded-tl-sm'
                                              }`}>
                                              {/* WhatsApp style reply quote */}
                                              <div className="mb-1.5 text-[10px] bg-black/5 px-2 py-1 rounded border-l-2 border-indigo-500/50 flex flex-col pointer-events-none select-none max-w-[200px] sm:max-w-[300px]">
                                                <span className="font-semibold text-indigo-600 truncate text-[9px]">{comment.authorRole === 'admin' ? 'Admin' : (comment.authorRole === 'customer' || comment.reviewType === 'external' ? (assignment?.customerName || assignment?.customer_name || comment.authorName || (comment.authorEmail ? comment.authorEmail.split('@')[0] : 'Customer')) : 'Creator')}</span>
                                                <span className="truncate text-gray-655 italic text-[11px] leading-tight">"{comment.message || comment.comment}"</span>
                                              </div>
                                              <p className="text-[13px] text-gray-800 break-words leading-snug">{rep.message || rep.text}</p>

                                              <div className="flex items-center justify-end gap-3 mt-1">
                                                {canReplyToEntry(rep) && !isOwnReply(rep) && (
                                                  <button
                                                    onClick={(e) => {
                                                      e.stopPropagation();
                                                      if (replyingToComment === comment.id) {
                                                        setReplyingToComment(null);
                                                        setReplyText('');
                                                        setEditingReplyId(null);
                                                      } else {
                                                        setReplyingToComment(comment.id);
                                                        setReplyText('');
                                                        setEditingReplyId(null);
                                                      }
                                                    }}
                                                    className="flex items-center gap-0.5 text-[9px] text-indigo-500 hover:text-indigo-700 transition-colors font-medium"
                                                  >
                                                    <MessageSquare className="h-2.5 w-2.5" /> Reply
                                                  </button>
                                                )}
                                                {rep.authorRole !== 'creator' && !rep.readByCreator && (
                                                  <button
                                                    onClick={(e) => {
                                                      e.stopPropagation();
                                                      handleMarkReplyRead(comment.id, rep.id);
                                                    }}
                                                    className="flex items-center gap-0.5 text-[9px] text-gray-500 hover:text-blue-500 transition-colors font-medium border border-gray-200 rounded px-1.5 py-0.5 bg-gray-50 hover:bg-blue-50"
                                                    title="Mark as Read"
                                                  >
                                                    <CheckCheck className="h-2.5 w-2.5 text-gray-400" /> Mark Read
                                                  </button>
                                                )}
                                                {rep.authorRole !== 'creator' && rep.readByCreator && (
                                                  <span className="flex items-center text-[9px] text-blue-500 font-medium gap-0.5" title="Read">
                                                    <CheckCheck className="h-2.5 w-2.5 text-blue-500" /> Read
                                                  </span>
                                                )}
                                                <span className="text-[9px] text-gray-500">
                                                  {rep.timestamp ? new Date(rep.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''}
                                                </span>

                                                {isCreator && (
                                                  <button
                                                    onClick={(e) => {
                                                      e.stopPropagation();
                                                      setReplyingToComment(comment.id);
                                                      setEditingReplyId(rep.id);
                                                      setReplyText(rep.message || rep.text);
                                                    }}
                                                    className="opacity-0 group-hover:opacity-100 transition-opacity text-[10px] text-emerald-700 hover:text-emerald-900 font-semibold"
                                                  >
                                                    Edit
                                                  </button>
                                                )}
                                                {isCreatorOwnedEntry(rep) && (
                                                  <button
                                                    onClick={(e) => {
                                                      e.stopPropagation();
                                                      handleVersionReplyDelete(comment.id, rep.id || '');
                                                    }}
                                                    className="opacity-0 group-hover:opacity-100 transition-opacity text-[10px] text-red-600 hover:text-red-800 font-semibold"
                                                  >
                                                    Delete
                                                  </button>
                                                )}
                                              </div>
                                            </div>
                                          </div>
                                        );
                                      });
                                    })()}

                                    {/* Reply input */}
                                    {replyingToComment === comment.id && (
                                      <div
                                        className="mt-1 flex flex-col self-end max-w-[90%] w-full bg-white p-2.5 rounded-2xl shadow-sm border border-indigo-100"
                                        onClick={(e) => e.stopPropagation()}
                                      >
                                        <div className="flex items-center gap-2 mb-1.5">
                                          <p className="text-[10px] text-indigo-500 font-medium">
                                            {editingReplyId ? 'Editing reply...' : 'New reply...'}
                                          </p>
                                        </div>
                                        <textarea
                                          value={replyText}
                                          onChange={(e) => setReplyText(e.target.value)}
                                          placeholder="Type a message..."
                                          rows={2}
                                          autoFocus
                                          className="w-full text-xs focus:outline-none resize-none bg-transparent placeholder-gray-400"
                                          onKeyDown={(e) => {
                                            if (e.key === 'Enter' && !e.shiftKey) {
                                              e.preventDefault();
                                              if (replyText.trim() && !replySubmitting) {
                                                handleVersionReplySubmit(comment.id);
                                              }
                                            }
                                          }}
                                        />
                                        <div className="flex items-center justify-end gap-2 mt-2">
                                          <button
                                            onClick={() => { setReplyingToComment(null); setEditingReplyId(null); setReplyText(''); }}
                                            className="text-[10px] text-gray-500 hover:text-gray-700 font-medium px-2 py-1"
                                          >
                                            Cancel
                                          </button>
                                          <button
                                            onClick={() => handleVersionReplySubmit(comment.id)}
                                            disabled={replySubmitting || !replyText.trim()}
                                            className="flex items-center justify-center w-7 h-7 bg-indigo-500 hover:bg-indigo-600 disabled:bg-indigo-300 text-white rounded-full transition-colors shadow-sm"
                                          >
                                            {replySubmitting
                                              ? <div className="animate-spin rounded-full h-3 w-3 border-2 border-white border-t-transparent" />
                                              : <Send className="h-3 w-3 ml-0.5" />}
                                          </button>
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>


          </div>

          {/* Right Column - Content Details & Assignment Info */}
          <div className="space-y-4 sm:space-y-6">
            {/* Assignment Details (Moved here) */}
            <div className="bg-white sm:rounded-lg sm:shadow-sm p-3.5 sm:p-4 border-b sm:border-0 border-gray-200">
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                <div>
                  <h1 className="text-lg font-bold text-gray-900">{assignment.title}</h1>
                  <p className="text-sm text-gray-600">for {assignment.customerName}</p>
                  {assignment.description && (
                    <p className="text-sm text-gray-600 mt-2">{assignment.description}</p>
                  )}
                </div>
                <div className="text-left sm:text-right">
                  <div className="flex items-center text-xs text-gray-500 mb-2 sm:justify-end">
                    <Calendar className="h-3 w-3 mr-1" />
                    {assignment.dueDate ? new Date(assignment.dueDate).toLocaleDateString() : 'N/A'}
                  </div>
                  <div className="flex items-center gap-1 flex-wrap sm:justify-end">
                    {(Array.isArray(assignment.platform) ? assignment.platform : [assignment.platform]).filter(Boolean).map((p, pi) => (
                      <span key={pi} className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded border text-xs font-medium ${p.toLowerCase() === 'facebook' ? 'bg-blue-100 text-blue-700 border-blue-200' :
                        p.toLowerCase() === 'instagram' ? 'bg-pink-100 text-pink-700 border-pink-200' :
                          p.toLowerCase() === 'youtube' ? 'bg-red-100 text-red-700 border-red-200' :
                            p.toLowerCase() === 'linkedin' ? 'bg-blue-50 text-blue-700 border-blue-200' :
                              p.toLowerCase() === 'twitter' ? 'bg-sky-100 text-sky-700 border-sky-200' :
                                'bg-gray-100 text-gray-700 border-gray-200'
                        }`}>
                        {p.toLowerCase() === 'facebook' ? <Facebook className="h-3 w-3" /> :
                          p.toLowerCase() === 'instagram' ? <Instagram className="h-3 w-3" /> :
                            p.toLowerCase() === 'linkedin' ? <Linkedin className="h-3 w-3" /> :
                              p.toLowerCase() === 'youtube' ? <Youtube className="h-3 w-3" /> :
                                p.toLowerCase() === 'twitter' ? <Twitter className="h-3 w-3" /> :
                                  <Globe className="h-3 w-3" />}
                        {p.charAt(0).toUpperCase() + p.slice(1)}
                      </span>
                    ))}
                    {assignment.postType && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded border text-xs font-semibold bg-amber-50 text-amber-700 border-amber-200 capitalize">
                        <FileText className="h-3 w-3" />
                        Type: {assignment.postType}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Content Details Form */}
            <div className="bg-white sm:rounded-lg sm:shadow-sm p-4 sm:p-6 border-b sm:border-0 border-gray-200">
              <h2 className="text-base sm:text-lg font-semibold mb-4 flex items-center">
                <MessageSquare className="h-5 w-5 mr-2 text-purple-600" />
                Content Details
              </h2>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Caption
                  </label>
                  <textarea
                    value={caption}
                    onChange={(e) => setCaption(e.target.value)}
                    rows={3}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    placeholder="Draft caption for the post (admin will review before it goes to customer)..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Tag className="h-4 w-4 inline mr-1" />
                    Hashtags
                  </label>
                  <input
                    type="text"
                    value={hashtags}
                    onChange={(e) => setHashtags(e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    placeholder="#fashion #summer #style"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Notes for Admin
                  </label>
                  <textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    rows={2}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    placeholder="Any specific instructions or context for the admin reviewing this content..."
                  />
                </div>
              </div>
            </div>

            {/* Notify Admin section moved to top of right column above — removed from here */}

            {/* Send to Customer — only visible when admin has approved latest version */}
            {(() => {
              const latestApproved = [...previousVersions].reverse().find(v => v.status === 'approved');
              if (!latestApproved) return null;
              return (
                <div className={`rounded-lg shadow-sm p-5 border-2 ${sentToCustomer ? 'bg-green-50 border-green-200' : 'bg-orange-50 border-orange-200'
                  }`}>
                  <div className="flex items-center gap-2 mb-2">
                    <ShieldCheck className={`h-5 w-5 ${sentToCustomer ? 'text-green-600' : 'text-orange-600'}`} />
                    <h3 className={`font-semibold text-sm ${sentToCustomer ? 'text-green-800' : 'text-orange-800'}`}>
                      {sentToCustomer ? 'Admin Directly Sent to Customer' : 'Admin Approved — Ready to Send'}
                    </h3>
                  </div>
                  {sentToCustomer ? (
                    <p className="text-xs text-green-700 mb-3">
                      The customer has been notified and can now review this content.
                    </p>
                  ) : (
                    <p className="text-xs text-orange-700 mb-3">
                      Admin has approved version {latestApproved.flowVersionNumber || latestApproved.versionNumber}. You can now send it to the customer for their review.
                    </p>
                  )}
                  {!sentToCustomer && (
                    <button
                      onClick={handleSendToCustomer}
                      disabled={sendingToCustomer}
                      className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-orange-500 hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-semibold rounded-lg transition-colors shadow-sm"
                    >
                      {sendingToCustomer ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                          Sending...
                        </>
                      ) : (
                        <>
                          <Send className="h-4 w-4" />
                          Send to Customer
                        </>
                      )}
                    </button>
                  )}
                </div>
              );
            })()}

            {/* Submit Button */}
            <div className="bg-white sm:rounded-lg sm:shadow-sm p-4 sm:p-6 border-b sm:border-0 border-gray-200">
              <div className="text-center">
                {isRevisionRequested && !hasModifiedMedia && (
                  <div className="text-xs text-red-700 bg-red-50 p-2.5 rounded-lg border border-red-200 font-medium flex items-start gap-2 mb-4 text-left">
                    <AlertCircle className="h-4 w-4 text-red-500 flex-shrink-0 mt-0.5 animate-bounce" />
                    <span>
                      <strong>Revisions Required:</strong> Please replace the requested media with a new version (or add new media) before submitting.
                    </span>
                  </div>
                )}
                <button
                  onClick={handleSubmit}
                  disabled={submitting || uploadedFiles.length === 0 || uploadedFiles.some(f => f.uploading) || (isRevisionRequested && !hasModifiedMedia)}
                  title={isRevisionRequested && !hasModifiedMedia ? "Please replace or add a new media version first" : ""}
                  className="w-full inline-flex items-center justify-center px-6 py-3 bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
                >
                  {submitting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Submitting...
                    </>
                  ) : (
                    <>
                      <Send className="h-4 w-4 mr-2" />
                      Submit to Admin for Review
                    </>
                  )}
                </button>
                {uploadedFiles.some(f => f.uploading) && (
                  <p className="text-xs text-gray-500 mt-2">
                    Please wait for all uploads to complete
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {submitSuccessModal.open && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={closeSubmitSuccessModal}>
          <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="bg-gradient-to-r from-emerald-500 to-green-600 px-6 py-5 text-white">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                  <CheckCircle className="h-6 w-6" />
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wider font-semibold text-emerald-100">Submission Confirmed</p>
                  <h3 className="text-lg font-bold leading-tight">{submitSuccessModal.title}</h3>
                </div>
              </div>
            </div>
            <div className="p-6 space-y-4">
              <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4">
                <p className="text-sm font-semibold text-emerald-800">Assignment Details</p>
                <p className="text-sm text-gray-900 mt-1 font-medium">{assignment?.title || 'Untitled Assignment'}</p>
                <div className="mt-2 space-y-1 text-xs text-gray-700">
                  <p><span className="font-semibold">Customer:</span> {assignment?.customerName || 'N/A'}</p>
                  <p><span className="font-semibold">Due:</span> {assignment?.dueDate ? new Date(assignment.dueDate).toLocaleDateString() : 'N/A'}</p>
                  <p><span className="font-semibold">Stage:</span> Admin Review</p>
                </div>
              </div>
              <p className="text-sm text-gray-700 leading-relaxed">{submitSuccessModal.message}</p>
              <button
                onClick={closeSubmitSuccessModal}
                className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold rounded-lg transition-colors"
              >
                <Check className="h-4 w-4" />
                Continue
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Media Preview Modal */}
      {selectedMedia && (
        <div
          className="fixed inset-0 bg-black bg-opacity-75 z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedMedia(null)}
        >
          <div className="relative max-w-7xl max-h-full" onClick={(e) => e.stopPropagation()}>
            {/* Close Button */}
            <button
              onClick={() => setSelectedMedia(null)}
              className="absolute -top-10 right-0 text-white hover:text-gray-300 transition-colors"
            >
              <X className="h-8 w-8" />
            </button>

            {/* Media Content */}
            <div className="bg-white rounded-lg overflow-hidden shadow-2xl">
              {selectedMedia.type === 'image' ? (
                <img
                  src={selectedMedia.preview}
                  alt={selectedMedia.name}
                  className="max-w-full max-h-[85vh] w-auto h-auto object-contain"
                />
              ) : (
                <video
                  src={selectedMedia.preview}
                  controls
                  autoPlay
                  className="max-w-full max-h-[85vh] w-auto h-auto"
                />
              )}

              {/* Media Info */}
              <div className="p-4 bg-gray-50 border-t">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-gray-900">{selectedMedia.name}</p>
                    <p className="text-sm text-gray-500">{formatFileSize(selectedMedia.size)}</p>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${selectedMedia.type === 'image'
                    ? 'bg-green-100 text-green-800'
                    : 'bg-blue-100 text-blue-800'
                    }`}>
                    {selectedMedia.type.toUpperCase()}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </ContentCreatorLayout>
  );
}

export default ContentUpload;
